import { useState, useEffect, useRef } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import {
  Box,
  Drawer,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Tooltip,
  IconButton,
  Stack,
  Chip,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import {
  formatDateForDisplay,
  formatDateForAPI,
  formatMuestraDateForAPI,
} from "../../utils/dateUtils";
import { notifyContestacionAvisosChanged } from "../../api/Estimates/contestacionAvisoApi";
import sampleImage from "../../img/logos/_signs02.png";
import EstimatesLineas from "./EstimatesLineas";
import SaveAsOutlinedIcon from "@mui/icons-material/SaveAsOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PriceChangeTwoToneIcon from "@mui/icons-material/PriceChangeTwoTone";
import EuroTwoToneIcon from "@mui/icons-material/EuroTwoTone";
import ReceiptTwoToneIcon from "@mui/icons-material/ReceiptTwoTone";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import DriveFolderUploadOutlinedIcon from "@mui/icons-material/DriveFolderUploadOutlined";

import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import {
  addPresupuesto,
  getPresupuestoById,
} from "../../api/Estimates/estimatesApi";
import { getUserById } from "../../api/User/userApi";
import { getEscandalloSubgrupos } from "../../api/Escandallo/escandalloApi";
import {
  uploadProjectFile,
  getFileContent,
  deleteFile,
} from "../../api/Proyectos/projectFilesApi";
import { getAllProyectos } from "../../api/Proyectos/proyectoApi";
import { getTemporadas } from "../../api/Temporadas/temporadaApi";
import {
  getProductosYMuestras,
  fetchProductIdentity,
} from "../../api/Muestras/productosYMuestrasApi";
import { getModelosTipo } from "../../api/Design/modelosTipoApi";
import { getClientes } from "../../api/Customer/customerApi";
import { getProveedores } from "../../api/ProveedoresyFabricas/proveedoresApi";
import {
  createBudgetLine,
  updateBudgetLine,
  deleteBudgetLine,
  fetchBudgetLines,
} from "../../api/Estimates/budgetLinesApi";
import BudgetPrint from "./BudgetPrint";
import BloqueProyectoEditar from "./BloqueProyectoEditar";
import CardDerechaBolas from "./CardDerechaBolas";
import EstimateEnviarOfertaClienteDialog from "./EstimateEnviarOfertaClienteDialog";
import EstimateOfertarChoiceDialog from "./EstimateOfertarChoiceDialog";
import {
  EstimateDuplicateBudgetDialog,
  EstimateDuplicateSuccessDialog,
} from "./EstimateDuplicateBudgetDialog";
import EstimateTommyOfferedDialog from "./EstimateTommyOfferedDialog";
import { isTommyClient } from "../../api/Estimates/estimateRfqApi";
import { printBudgetPdf } from "../../utils/printBudgetPdf";
import {
  applyIdentityToPresupuesto,
  buildCopyPresupuestoNombre,
  buildPresupuestoIdentityPatch,
  getProductIdentityFields,
  getProductSupplyFields,
  findCatalogItem,
  hasAnyPresupuestoProductIdentity,
  hasPresupuestoProductIdentity,
  hasPresupuestoVol,
  isEstimateLocked,
  mergePresupuestoWithCatalogItem,
} from "../../utils/newBudgetDrawerUtils";
import {
  resolveDutyPct,
  resolveProveedorForDuty,
} from "../../utils/estimatesImportUtils";
import { getDdpImportTarifas } from "../../api/Estimates/ddpImportTarifasApi";
import {
  buildDdpImportLines,
  PER_SHIPMENT_SUBGRUPO_IDS,
} from "../../utils/ddpImportTarifasPure";

import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import VentanaPresupuestosHistoricos from "./VentanaPresupuestosHistoricos";
import {
  pageHeroShellSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";

const API_URL = process.env.REACT_APP_API_URL;

function buildDebouncedPresupuestoPutPayload(
  newPresupuesto,
  productosYMuestras,
  modelosTipo,
  localNotas,
) {
  const identity = buildPresupuestoIdentityPatch(
    newPresupuesto,
    productosYMuestras,
    modelosTipo,
  );
  const resolved = hasPresupuestoProductIdentity(newPresupuesto)
    ? newPresupuesto
    : identity;

  const payload = {
    Units: newPresupuesto.Units || 0,
    PVP: newPresupuesto.PVP || null,
    CustomerTarget: newPresupuesto.CustomerTarget || null,
    MarkUp: newPresupuesto.MarkUp || null,
    Notas: newPresupuesto.Notas ?? localNotas ?? "",
    Id_Incoterm: newPresupuesto.Id_Incoterm || null,
    IdSubgrupo: newPresupuesto.IdSubgrupo || null,
  };

  if (hasAnyPresupuestoProductIdentity(resolved)) {
    if (resolved.TipoPrenda) payload.TipoPrenda = resolved.TipoPrenda;
    if (resolved.ABREVIATURA) payload.ABREVIATURA = resolved.ABREVIATURA;
    if (hasPresupuestoVol(resolved.VOL)) payload.VOL = resolved.VOL;
  }

  if (newPresupuesto.IdProducto || newPresupuesto.IdMuestra) {
    payload.IdProducto = newPresupuesto.IdProducto || null;
    payload.IdMuestra = newPresupuesto.IdMuestra || null;
  }

  return payload;
}

const NewBudgetDrawer = ({
  open,
  onClose,
  onBudgetAdded,
  budget,
  escandalloGrupos = [], // Array de grupos
  escandalloSubgrupos = [], // Array de subgrupos
  existingLines = [], // Líneas iniciales del presupuesto
  handleSaveLines, // Función para guardar las líneas
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [productosYMuestras, setProductosYMuestras] = useState([]);
  const [modelosTipo, setModelosTipo] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [temporadas, setTemporadas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState("");
  const [newPresupuesto, setNewPresupuesto] = useState({
    ProyectoId: "",
    NombrePresupuesto: "",
    TemporadaId: "",
    DiseñoMuestra: "",
    IdCliente: "",
    IdMarca: "",
    IdProveedor: null,
    FechaPresentacion: null,
    FechaAceptacion: null,
    FechaMaximaContestacion: null,
    Units: 0,
    Notas: "",
    designCliente: "",
    CustomerTarget: "",
    PVP: "",
    MarkUp: "",
    Id_Incoterm: "",
    IdProducto: null,
    IdMuestra: null,
    TipoPrenda: null,
    ABREVIATURA: null,
    VOL: null,
    IdSubgrupo: null, // Añadir IdSubgrupo para el escandallo
  });
  const [proveedores, setProveedores] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false); // Estado del diálogo
  const [confirmUnlockOpen, setConfirmUnlockOpen] = useState(false);
  const isEditMode = !!budget; // Detectar si está en modo edición
  const [lines, setLines] = useState([]);
  const linesRef = useRef([]);
  const identityResolvedRef = useRef(null);
  useEffect(() => {
    linesRef.current = lines;
  }, [lines]);
  const [isEstimatesLocked, setIsEstimatesLocked] = useState(false);
  const [permisoAbrirEstimate, setPermisoAbrirEstimate] = useState(null);
  const [confirmStateChange, setConfirmStateChange] = useState(null);
  const [clientOfferOpen, setClientOfferOpen] = useState(false);
  const [ofertarChoiceOpen, setOfertarChoiceOpen] = useState(false);
  const [ofertarPrintBusy, setOfertarPrintBusy] = useState(false);
  const [tommyOfferedOpen, setTommyOfferedOpen] = useState(false);
  const [tommyOfferedConfirming, setTommyOfferedConfirming] = useState(false);

  const [incoterms, setIncoterms] = useState([]);
  const [subgruposFromApi, setSubgruposFromApi] = useState([]);

  const [uploadedFiles, setUploadedFiles] = useState([]);

  /*  const [searchTerm, setSearchTerm] = useState(""); */
  const [searchTerm] = useState("");
  /* NOTAS */
  const [localNotas, setLocalNotas] = useState("");
  const [confirmDuplicateOpen, setConfirmDuplicateOpen] = useState(false);
  const [duplicateSuccessOpen, setDuplicateSuccessOpen] = useState(false);
  const [duplicateSuccessNombre, setDuplicateSuccessNombre] = useState("");
  const [duplicateFechaMaxima, setDuplicateFechaMaxima] = useState("");
  const [duplicateFechaError, setDuplicateFechaError] = useState(false);
  const [duplicateBusy, setDuplicateBusy] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);
  const [fechaContestacionLoaded, setFechaContestacionLoaded] = useState(false);
  const fechaContestacionRef = useRef(null);
  const canUnlockEstimate = permisoAbrirEstimate === true;

  // Ordenar proyectos alfabéticamente por subject
  const sortedProyectos = [...proyectos].sort((a, b) =>
    (a.subject || "").localeCompare(b.subject || "", undefined, {
      sensitivity: "base",
    }),
  );

  // Filtrar por cliente seleccionado y término de búsqueda
  const filteredProyectos = sortedProyectos.filter((proyecto) => {
    const matchesCliente =
      !selectedCliente ||
      String(proyecto.customerId) === String(selectedCliente);
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      !term ||
      proyecto.subject.toLowerCase().includes(term) ||
      (proyecto.notes || "").toLowerCase().includes(term);
    return matchesCliente && matchesSearch;
  });

  // MOSTRAR PRESUPUESTOS SIMILARES
  const [openHistoricos, setOpenHistoricos] = useState(false);

  useEffect(() => {
    if (existingLines.length > 0) {
      setLines(existingLines);
    }
  }, [existingLines]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    let userId = null;
    if (token) {
      try {
        const decoded = jwtDecode(token);
        userId = decoded.userId;
      } catch {
        /* ignore */
      }
    }
    if (!userId) {
      setPermisoAbrirEstimate(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const user = await getUserById(userId);
        if (!cancelled) {
          setPermisoAbrirEstimate(Boolean(user?.Permiso_AbrirEstimate));
        }
      } catch (error) {
        console.error("Error cargando Permiso_AbrirEstimate:", error);
        if (!cancelled) setPermisoAbrirEstimate(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (budget && open) {
      const catalogItem = findCatalogItem(productosYMuestras, {
        idProducto: budget.IdProducto,
        idMuestra: budget.IdMuestra,
        nombre: budget.DiseñoMuestra,
      });
      const nombreDiseño = catalogItem?.Nombre || budget.DiseñoMuestra || "";

      setNewPresupuesto(
        mergePresupuestoWithCatalogItem(
          {
            ...budget,
            IdPresupuesto: budget.IdPresupuesto || "",
            ProyectoId: budget.IdProyectoCab || "",
            NombrePresupuesto: budget.NombrePresupuesto || "",
            TemporadaId: budget.TemporadaId || budget.Temporada || "",
            DiseñoMuestra: nombreDiseño,
            IdCliente: budget.IdCliente || "",
            IdMarca: budget.IdMarca || "",
            IdProveedor: budget.IdProveedor || null,
            FechaPresentacion: budget?.FechaPresentacion
              ? formatDateForDisplay(budget.FechaPresentacion)
              : null,
            FechaAceptacion: budget?.FechaAceptacion
              ? formatDateForDisplay(budget.FechaAceptacion)
              : null,
            FechaCrear: budget?.FechaCrear
              ? formatDateForDisplay(budget.FechaCrear)
              : null,
            FechaCancelacion: budget?.FechaCancelacion
              ? formatDateForDisplay(budget.FechaCancelacion)
              : null,
            FechaMaximaContestacion:
              formatMuestraDateForAPI(budget?.FechaMaximaContestacion) ||
              (fechaContestacionRef.current?.id === budget.IdPresupuesto
                ? fechaContestacionRef.current.ymd
                : null),
            Estado: budget.PresupuestoEstado || "Created",
            Units: budget.Units || 0,
            Notas: budget?.Notas || "",
            designCliente: budget?.designCliente || "",
            CustomerTarget: budget?.CustomerTarget || "",
            PVP: budget?.PVP ?? "",
            MarkUp: budget?.MarkUp || "",
            Id_Incoterm: budget?.Id_Incoterm || "",
            TipoPrenda: budget?.TipoPrenda || null,
            ABREVIATURA: budget?.ABREVIATURA || null,
            VOL: budget?.VOL ?? null,
            IdSubgrupo: budget?.IdSubgrupo || null,
          },
          catalogItem,
          modelosTipo,
        ),
      );

      setSelectedCliente(budget.IdCliente || "");
    } else if (!budget && open) {
      setNewPresupuesto({
        ProyectoId: "",
        NombrePresupuesto: "",
        TemporadaId: "",
        DiseñoMuestra: "",
        IdCliente: "",
        IdMarca: "",
        IdProveedor: null,
        FechaPresentacion: null,
        FechaAceptacion: null,
        FechaCrear: null,
        FechaCancelacion: null,
        FechaMaximaContestacion: null,
        Estado: "Created",
        Units: 0,
        Notas: "",
        designCliente: "",
        CustomerTarget: "",
        PVP: "",
        MarkUp: "",
        Id_Incoterm: "",
        TipoPrenda: "",
        ABREVIATURA: "",
        VOL: "",
        IdProducto: null,
      });
      setSelectedCliente("");
    }
  }, [budget, open, productosYMuestras, modelosTipo]);

  useEffect(() => {
    if (!open) return undefined;
    if (!budget?.IdPresupuesto) {
      setFechaContestacionLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setFechaContestacionLoaded(false);
    (async () => {
      try {
        const row = await getPresupuestoById(budget.IdPresupuesto);
        if (cancelled || !row) return;
        const ymd =
          formatMuestraDateForAPI(row.FechaMaximaContestacion) || null;
        fechaContestacionRef.current = {
          id: budget.IdPresupuesto,
          ymd,
        };
        setNewPresupuesto((prev) => {
          if (String(prev?.IdPresupuesto) !== String(budget.IdPresupuesto)) {
            return prev;
          }
          return {
            ...prev,
            FechaMaximaContestacion: prev.FechaMaximaContestacion || ymd,
          };
        });
      } catch (error) {
        console.error("Fecha máxima contestación:", error);
      } finally {
        if (!cancelled) setFechaContestacionLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, budget?.IdPresupuesto]);

  // === Validación de campos obligatorios (mismo criterio que en BloqueProyectoEditar) ===
  const requiredState = {
    IdCliente: !!(selectedCliente ?? newPresupuesto?.IdCliente),
    IdMarca: !!newPresupuesto?.IdMarca,
    ProyectoId: !!newPresupuesto?.ProyectoId,
    IdProveedor: !!newPresupuesto?.IdProveedor,
    TemporadaId: !!newPresupuesto?.TemporadaId,
    designCliente: !!(newPresupuesto?.designCliente ?? "").trim(),
    IdSubgrupo: !!newPresupuesto?.IdSubgrupo,
    Id_Incoterm: !!newPresupuesto?.Id_Incoterm,
    ...(!newPresupuesto?.IdPresupuesto
      ? {
          FechaMaximaContestacion: !!formatMuestraDateForAPI(
            newPresupuesto?.FechaMaximaContestacion,
          ),
        }
      : {}),
  };

  const labelByKey = {
    IdCliente: t("EstimatesPage.drawer.client"),
    IdMarca: t("EstimatesPage.drawer.brand"),
    ProyectoId: t("EstimatesPage.drawer.project"),
    IdProveedor: t("EstimatesPage.drawer.supplier"),
    TemporadaId: t("EstimatesPage.drawer.season"),
    designCliente: t("EstimatesPage.drawer.clientDesignName"),
    IdSubgrupo: t("EstimatesPage.drawer.material"),
    Id_Incoterm: t("EstimatesPage.drawer.incoterm"),
    FechaMaximaContestacion: t("EstimatesPage.contestacion.title"),
  };

  const missingKeys = Object.entries(requiredState)
    .filter(([, ok]) => !ok)
    .map(([k]) => k);

  const canCreate = missingKeys.length === 0;

  const handleCloseDrawer = async () => {
    const n = (v) => {
      const t = typeof v === "string" ? v.replace(",", ".") : v;
      const x = Number(t);
      return Number.isFinite(x) ? x : 0;
    };

    const canPersist = (l) =>
      Number(l.ESCANDALLO_GRUPOS) > 0 &&
      Number(l.ESCANDALLO_SUBGRUPO) > 0 &&
      // si no es % (192/209/210), exigimos Un > 0
      (![192, 209, 210].includes(Number(l.ESCANDALLO_SUBGRUPO))
        ? n(l.Un) > 0
        : true);

    try {
      // guarda cabecera “ya mismo” (por si el debounce aún no ha disparado)
      if (newPresupuesto.IdPresupuesto) {
        await axios.put(
          `${API_URL}/api/proyectos/${newPresupuesto.ProyectoId}/presupuestos/${newPresupuesto.IdPresupuesto}`,
          buildDebouncedPresupuestoPutPayload(
            newPresupuesto,
            productosYMuestras,
            modelosTipo,
            localNotas,
          ),
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
      }

      // Usa el snapshot más reciente compartido por el hijo para no perder
      // la última edición si el drawer se cierra muy rápido.
      const latestLines = Array.isArray(linesRef.current)
        ? linesRef.current
        : lines;

      const ops = latestLines.filter(canPersist).map((line) => {
        const payload = {
          ESCANDALLO_GRUPOS: Number(line.ESCANDALLO_GRUPOS),
          ESCANDALLO_SUBGRUPO: Number(line.ESCANDALLO_SUBGRUPO),
          Proveedor: line.PROVEEDOR_ESCANDALLO || null,
          Un: n(line.Un) || 1,
          Coste: n(line.Coste) || 0,
          Venta: n(line.Venta) || 0,
          MargenCoste: line.MargenCoste != null ? n(line.MargenCoste) : null,
          MargenVenta: line.MargenVenta != null ? n(line.MargenVenta) : null,
          MargenUn: line.MargenUn != null ? n(line.MargenUn) : null,
          MonedaCoste: line.MonedaCoste || "EUR",
          MonedaVenta: line.MonedaVenta || "EUR",
          TipoCambio: n(line.TipoCambio) || 1,
          CosteConvertido: n(line.CosteConvertido) || 0,
        };

        return line.IdLineaPresupuestos
          ? updateBudgetLine(line.IdLineaPresupuestos, payload)
          : createBudgetLine(newPresupuesto.IdPresupuesto, payload);
      });

      // no bloquees el cierre si alguna petición falla
      const results = await Promise.allSettled(ops);
      const fails = results.filter((r) => r.status === "rejected").length;
      if (fails) {
        console.warn(
          `Guardado con ${fails} línea(s) fallidas (no bloqueante).`,
        );
      }
    } catch (error) {
      console.error("Error al guardar las líneas:", error);
      // No alert; no bloquees el cierre
    } finally {
      onClose();
    }
  };

  /* Carga los Productos Y Muestras */
  useEffect(() => {
    const fetchProductosYMuestras = async () => {
      try {
        const data = await getProductosYMuestras();

        const normalizados = data.map((item) => ({
          ...item,
          tipo: item.Tipo,
          id: item.Id,
          nombre: item.Nombre,
        }));

        setProductosYMuestras(
          normalizados.sort((a, b) =>
            (a.Nombre || "").localeCompare(b.Nombre || "", undefined, {
              sensitivity: "base",
            }),
          ),
        );
      } catch (error) {
        console.error("Error cargando productos y muestras:", error);
      }
    };

    fetchProductosYMuestras();
  }, []);

  useEffect(() => {
    const fetchModelosTipo = async () => {
      try {
        const data = await getModelosTipo();
        setModelosTipo(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error cargando VolumenPrenda (modelosTipo):", error);
      }
    };

    fetchModelosTipo();
  }, []);

  useEffect(() => {
    if (!open || !productosYMuestras.length) return;

    setNewPresupuesto((prev) => {
      const item = findCatalogItem(productosYMuestras, {
        idProducto: prev.IdProducto,
        idMuestra: prev.IdMuestra,
        nombre: prev.DiseñoMuestra,
      });
      if (!item) return prev;

      const merged = mergePresupuestoWithCatalogItem(prev, item, modelosTipo);
      const unchanged =
        merged.TipoPrenda === prev.TipoPrenda &&
        merged.ABREVIATURA === prev.ABREVIATURA &&
        String(merged.VOL ?? "") === String(prev.VOL ?? "");
      return unchanged ? prev : merged;
    });
  }, [
    open,
    productosYMuestras,
    modelosTipo,
    budget?.IdPresupuesto,
    budget?.IdProducto,
    budget?.IdMuestra,
  ]);

  useEffect(() => {
    if (!open) {
      identityResolvedRef.current = null;
      return;
    }

    const nombre = newPresupuesto?.DiseñoMuestra || budget?.DiseñoMuestra || "";
    const idProducto = newPresupuesto?.IdProducto ?? budget?.IdProducto;
    const idMuestra = newPresupuesto?.IdMuestra ?? budget?.IdMuestra;
    const idPresupuesto =
      newPresupuesto?.IdPresupuesto || budget?.IdPresupuesto;
    const proyectoId = newPresupuesto?.ProyectoId || budget?.IdProyectoCab;

    if (
      hasPresupuestoProductIdentity(newPresupuesto) ||
      (!nombre && !idProducto && !idMuestra)
    ) {
      return;
    }

    const fetchKey = `${idPresupuesto || "new"}:${nombre}:${idProducto || ""}:${idMuestra || ""}:c${productosYMuestras.length}:m${modelosTipo.length}`;
    if (identityResolvedRef.current === fetchKey) return;
    identityResolvedRef.current = fetchKey;

    let cancelled = false;
    (async () => {
      try {
        let identity = null;

        if (idPresupuesto) {
          try {
            const fresh = await getPresupuestoById(idPresupuesto);
            if (hasPresupuestoProductIdentity(fresh)) {
              identity = {
                TipoPrenda: fresh.TipoPrenda,
                ABREVIATURA: fresh.ABREVIATURA,
                VOL: fresh.VOL,
                IdProducto: fresh.IdProducto,
                IdMuestra: fresh.IdMuestra,
              };
            }
          } catch (error) {
            console.warn(
              "No se pudo refrescar identity desde presupuesto:",
              error,
            );
          }
        }

        if (!identity || !hasPresupuestoProductIdentity(identity)) {
          try {
            identity = await fetchProductIdentity({
              nombre,
              idProducto,
              idMuestra,
            });
          } catch (error) {
            console.warn("fetchProductIdentity falló:", error);
          }
        }

        if (!identity || !hasPresupuestoProductIdentity(identity)) {
          const item = findCatalogItem(productosYMuestras, {
            idProducto,
            idMuestra,
            nombre,
          });
          const fromClient = getProductIdentityFields(item, modelosTipo);
          if (hasPresupuestoProductIdentity(fromClient)) {
            identity = fromClient;
          }
        }

        if (cancelled || !hasAnyPresupuestoProductIdentity(identity)) return;

        setNewPresupuesto((prev) => applyIdentityToPresupuesto(prev, identity));

        if (idPresupuesto && proyectoId) {
          await axios.put(
            `${API_URL}/api/proyectos/${proyectoId}/presupuestos/${idPresupuesto}`,
            {
              TipoPrenda: identity.TipoPrenda,
              ABREVIATURA: identity.ABREVIATURA,
              VOL: identity.VOL,
              IdProducto: identity.IdProducto ?? idProducto ?? null,
              IdMuestra: identity.IdMuestra ?? idMuestra ?? null,
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          );
        }
      } catch (error) {
        console.error("Error resolviendo product identity:", error);
        identityResolvedRef.current = null;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    budget?.IdPresupuesto,
    budget?.DiseñoMuestra,
    budget?.IdProducto,
    budget?.IdMuestra,
    budget?.IdProyectoCab,
    newPresupuesto?.DiseñoMuestra,
    newPresupuesto?.IdProducto,
    newPresupuesto?.IdMuestra,
    newPresupuesto?.IdPresupuesto,
    newPresupuesto?.ProyectoId,
    productosYMuestras,
    modelosTipo,
  ]);

  /* Carga los proyectos */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const proyectosData = await getAllProyectos();
        setProyectos(proyectosData);

        const temporadasData = await getTemporadas();
        setTemporadas(
          [...temporadasData].sort((a, b) =>
            (a.TEMPORADA || "").localeCompare(b.TEMPORADA || "", undefined, {
              sensitivity: "base",
            }),
          ),
        );
      } catch (error) {
        console.error("Error al cargar proyectos o temporadas:", error);
      }
    };

    fetchData();
  }, []);

  /* Carga los fetchClientes */
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const response = await getClientes(); // Implementa esta función si aún no existe

        setClientes(
          [...(response || [])]
            .filter(
              (c) => !(c.name || "").trim().toUpperCase().includes("INDITEX"),
            )
            .sort((a, b) =>
              (a.name || "").localeCompare(b.name || "", undefined, {
                sensitivity: "base",
              }),
            ),
        );
      } catch (error) {
        console.error("Error al cargar clientes:", error);
      }
    };

    fetchClientes();
  }, []);

  // Carga los proveedores al montar el componente
  useEffect(() => {
    const fetchProveedores = async () => {
      try {
        const data = await getProveedores();
        const sortedData = [...data].sort((a, b) =>
          (a.N_PROVEEDOR || "").localeCompare(b.N_PROVEEDOR || "", undefined, {
            sensitivity: "base",
          }),
        ); // Ordenar alfabéticamente
        setProveedores(sortedData);
      } catch (error) {
        console.error("Error al cargar proveedores:", error);
      }
    };

    fetchProveedores();
  }, []);

  // Carga las marcas al montar el componente
  useEffect(() => {
    const fetchMarcas = async () => {
      if (selectedCliente) {
        try {
          const response = await axios.get(
            `${API_URL}/api/marcas-por-cliente/${selectedCliente}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          );
          setMarcas(
            [...(response.data || [])].sort((a, b) =>
              (a.MARCA || "").localeCompare(b.MARCA || "", undefined, {
                sensitivity: "base",
              }),
            ),
          );
        } catch (error) {
          console.error("Error al cargar marcas:", error);
        }
      } else {
        setMarcas([]);
      }
    };

    fetchMarcas();
  }, [selectedCliente]); // Recalcula cuando cambia el cliente

  // MATERIAL Carga los escandallo subgrupos al montar el componente
  useEffect(() => {
    const fetchSubgrupos = async () => {
      try {
        const data = await getEscandalloSubgrupos();
        setSubgruposFromApi(
          [...(data || [])].sort((a, b) =>
            (a.SUBGRUPO || "").localeCompare(b.SUBGRUPO || "", undefined, {
              sensitivity: "base",
            }),
          ),
        );
      } catch (error) {
        console.error("Error al cargar escandalloSubgrupos:", error);
      }
    };

    fetchSubgrupos();
  }, []);

  // Solo desbloquea cuando el estado es "Created"
  useEffect(() => {
    setIsEstimatesLocked(isEstimateLocked(newPresupuesto.Estado));
  }, [newPresupuesto.Estado]);

  // Carga los incoterms al montar el componente
  useEffect(() => {
    const fetchIncoterms = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/incoterms`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setIncoterms(
          [...(response.data || [])].sort((a, b) =>
            (a.Incoterm || "").localeCompare(b.Incoterm || "", undefined, {
              sensitivity: "base",
            }),
          ),
        );
      } catch (error) {
        console.error("Error al cargar los incoterms:", error);
      }
    };

    fetchIncoterms();
  }, []);

  // Este archivo contiene el nuevo useEffect para insertar líneas DDP con la lógica avanzada que me pasaste

  // Helpers locales (puedes dejarlos al principio del archivo de las funciones)
  // ===== Helpers para totales =====
  const IMPORT_GROUP_ID = 13;
  const PER_SHIPMENT_IMPORT_IDS = PER_SHIPMENT_SUBGRUPO_IDS;
  const PERCENT_IDS = new Set([192, 209, 210]); // ARANCEL, CAUTION, INSURANCE (Un es %)
  const n = (v) => {
    const t = typeof v === "string" ? v.replace(",", ".") : v;
    const x = Number(t);
    return Number.isFinite(x) ? x : 0;
  };

  useEffect(() => {
    const DDP_ID = 4;

    const idPresupuesto = parseInt(newPresupuesto.IdPresupuesto, 10);
    if (!idPresupuesto || isNaN(idPresupuesto)) return;

    const subgruposIDs = {
      INSURANCE: 210,
      LCL: 203,
      ARANCEL: 192,
      CAUTION: 209,
      HANDLING: 204,
      DELIVERY: 198,
      DOCS: 205,
      IES: 206,
      CUSTOMS: 193,
      T3: 208,
    };

    const subgruposImport = Object.values(subgruposIDs);
    const safeDecimal = (val, decimals = 4) =>
      Number(parseFloat(val).toFixed(decimals));

    const gestionarLineasImportacion = async () => {
      if (!proveedores.length) return;

      /*  */
      // 0) Persistir ya las líneas locales NO import (GRUPO != 13) que aún no estén en BD
      const n = (v) => {
        const t = typeof v === "string" ? v.replace(",", ".") : v;
        const x = Number(t);
        return Number.isFinite(x) ? x : 0;
      };

      const localUnsavedNotImport = (
        Array.isArray(linesRef.current) ? linesRef.current : []
      ).filter(
        (l) =>
          !l?.IdLineaPresupuestos &&
          Number(l.ESCANDALLO_GRUPOS) !== IMPORT_GROUP_ID &&
          Number(l.ESCANDALLO_GRUPOS) > 0 &&
          Number(l.ESCANDALLO_SUBGRUPO) > 0 &&
          n(l.Un) > 0,
      );

      for (const l of localUnsavedNotImport) {
        try {
          await createBudgetLine(idPresupuesto, {
            ESCANDALLO_GRUPOS: Number(l.ESCANDALLO_GRUPOS),
            ESCANDALLO_SUBGRUPO: Number(l.ESCANDALLO_SUBGRUPO),
            Proveedor: l.PROVEEDOR_ESCANDALLO || null,
            Un: n(l.Un) || 1,
            Coste: n(l.Coste) || 0,
            Venta: n(l.Venta) || 0,
            MargenCoste: l.MargenCoste != null ? n(l.MargenCoste) : null,
            MargenVenta: l.MargenVenta != null ? n(l.MargenVenta) : 10,
            MargenUn: l.MargenUn != null ? n(l.MargenUn) : null,
            MonedaCoste: l.MonedaCoste || "EUR",
            MonedaVenta: l.MonedaVenta || "EUR",
            TipoCambio: n(l.TipoCambio) || 1,
            CosteConvertido: n(l.CosteConvertido) || 0,
          });
        } catch (e) {
          console.warn(
            "No se pudo persistir línea !13 local:",
            e?.response?.status || e,
          );
        }
      }

      /*  */

      const parse = (val) => parseFloat(String(val).replace(",", ".")) || 0;
      /* si cambio la linea comentada por la de abajo, calcula las Units por m/3 reales*/
      /* const totalM3 = parse(newPresupuesto.VOL) * parse(newPresupuesto.Units); */
      const totalM3 = parse(newPresupuesto.VOL);
      /*  */

      const cantidadM3 = safeDecimal(totalM3);

      const localLines = Array.isArray(linesRef.current)
        ? linesRef.current
        : [];

      const [subgrupos, tarifas, realLines] = await Promise.all([
        getEscandalloSubgrupos(),
        getDdpImportTarifas(),
        fetchBudgetLines(idPresupuesto),
      ]);

      const supplierFromLine =
        localLines.find((l) => Number(l?.ESCANDALLO_GRUPOS) !== IMPORT_GROUP_ID)
          ?.PROVEEDOR_ESCANDALLO ||
        realLines.find((l) => l.ESCANDALLO_GRUPOS !== IMPORT_GROUP_ID)
          ?.Proveedor;

      const proveedor = resolveProveedorForDuty(
        newPresupuesto,
        proveedores,
        [...localLines, ...realLines],
        supplierFromLine || newPresupuesto.IdProveedor,
      );
      const pais = proveedor?.PAIS?.toLowerCase() || "";

      const subtotalProveedor = realLines
        .filter((l) => l.ESCANDALLO_GRUPOS !== IMPORT_GROUP_ID)
        .reduce((acc, l) => acc + parse(l.CosteConvertido), 0);

      const subgrupoMaterial = subgrupos.find(
        (s) => s.ID_SUBGRUPO === newPresupuesto.IdSubgrupo,
      );
      const dutyPct = resolveDutyPct(pais, subgrupoMaterial);

      const allImportLines = buildDdpImportLines({
        volPerUnit: cantidadM3,
        subtotalProveedor,
        dutyPct,
        tarifas,
      });

      for (const line of allImportLines) {
        const existing = realLines.find(
          (l) =>
            l.ESCANDALLO_GRUPOS === IMPORT_GROUP_ID &&
            l.ESCANDALLO_SUBGRUPO === line.ID_SUBGRUPO,
        );

        const venta = safeDecimal(line.CosteConvertido / 0.9);
        const payload = {
          ESCANDALLO_GRUPOS: IMPORT_GROUP_ID,
          ESCANDALLO_SUBGRUPO: line.ID_SUBGRUPO,
          Un: safeDecimal(line.Un),
          Coste: safeDecimal(line.Coste, 2),
          CosteConvertido: safeDecimal(line.CosteConvertido),
          MonedaCoste: "EUR",
          MonedaVenta: "EUR",
          TipoCambio: 1,
          Venta: venta,
          MargenVenta: 10,
          MargenCoste: null,
          MargenUn: null,
          PROVEEDOR_ESCANDALLO: null,
        };

        if (!existing) {
          await createBudgetLine(idPresupuesto, payload);
        } else {
          await updateBudgetLine(existing.IdLineaPresupuestos, payload);
        }
      }

      const updatedLines = (await fetchBudgetLines(idPresupuesto)).map(
        (line) => ({
          ...line,
          Un:
            line.Un !== null
              ? safeDecimal(String(line.Un).replace(",", "."))
              : 0,
        }),
      );

      setLines(updatedLines);
      setRefreshKey((prev) => prev + 1);
      if (handleSaveLines) handleSaveLines(updatedLines);
    };

    const eliminarLineasImport = async () => {
      const realLines = await fetchBudgetLines(idPresupuesto);
      const toDelete = realLines.filter(
        (l) =>
          l.ESCANDALLO_GRUPOS === IMPORT_GROUP_ID &&
          subgruposImport.includes(l.ESCANDALLO_SUBGRUPO),
      );

      for (const line of toDelete) {
        await deleteBudgetLine(line.IdLineaPresupuestos);
      }

      const updatedLines = await fetchBudgetLines(idPresupuesto);
      setLines(updatedLines);
      setRefreshKey((prev) => prev + 1);
      if (handleSaveLines) handleSaveLines(updatedLines);
    };

    const idIncoterm = parseInt(newPresupuesto.Id_Incoterm, 10);
    if (idIncoterm === DDP_ID) {
      gestionarLineasImportacion();
    } else {
      eliminarLineasImport();
    }
  }, [
    newPresupuesto.Id_Incoterm,
    newPresupuesto.Units,
    newPresupuesto.VOL,
    newPresupuesto.IdPresupuesto,
    newPresupuesto.IdProveedor,
    newPresupuesto.IdSubgrupo,
    proveedores,
  ]);

  /*  */

  /* INSERTA elimina  LINEAS si es Incoterm DDP */

  const handleUnlockBudget = async () => {
    if (!newPresupuesto.IdPresupuesto) return;
    if (!canUnlockEstimate) {
      alert(t("EstimatesPage.drawer.errorUnlockPermission"));
      return;
    }

    try {
      // 1️⃣ Primero, cambiar el estado a "Created"
      await axios.put(
        `${API_URL}/api/presupuestos/${newPresupuesto.IdPresupuesto}/estado`,
        {
          estado: "Created",
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      // 2️⃣ Luego, llamar al nuevo endpoint para resetear las fechas
      await axios.put(
        `${API_URL}/api/presupuestos/${newPresupuesto.IdPresupuesto}/reset-fechas`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      // 3️⃣ Actualizar estado en el frontend
      setNewPresupuesto((prev) => ({
        ...prev,
        Estado: "Created",
        FechaPresentacion: null,
        FechaAceptacion: null,
        FechaCancelacion: null,
      }));

      setIsEstimatesLocked(false); // Desbloquear el presupuesto en la UI
      setConfirmUnlockOpen(false); // Cerrar el diálogo
    } catch (error) {
      console.error("Error al desbloquear el presupuesto:", error);
      alert(t("EstimatesPage.drawer.errorUnlock"));
    }
  };

  /* calcula el MarkUp */
  useEffect(() => {
    const target = parseFloat(newPresupuesto.CustomerTarget) || 0;
    const pvp = parseFloat(newPresupuesto.PVP) || 0;

    if (target > 0 && pvp > 0) {
      const newMarkUp = (pvp / target).toFixed(2);

      if (newPresupuesto.MarkUp !== newMarkUp) {
        setNewPresupuesto((prev) => ({
          ...prev,
          MarkUp: newMarkUp,
        }));
      }
    } else {
      // Si falta algún valor, MarkUp debe ser null
      if (newPresupuesto.MarkUp !== null) {
        setNewPresupuesto((prev) => ({
          ...prev,
          MarkUp: null,
        }));
      }
    }
  }, [newPresupuesto.CustomerTarget, newPresupuesto.PVP]);

  /* Guardar cambios en la API después de 1 segundo sin spam (Debounce) */
  useEffect(() => {
    if (!newPresupuesto.IdPresupuesto) return; // Evitar ejecutar si no hay presupuesto
    const delayDebounce = setTimeout(async () => {
      try {
        await axios.put(
          `${API_URL}/api/proyectos/${newPresupuesto.ProyectoId}/presupuestos/${newPresupuesto.IdPresupuesto}`,
          buildDebouncedPresupuestoPutPayload(
            newPresupuesto,
            productosYMuestras,
            modelosTipo,
            localNotas,
          ),
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
      } catch (error) {
        console.error("Error al guardar el presupuesto:", error);
        // alert("Error al guardar el presupuesto.");  // ❌ Elimina el alert
      }
    }, 1000); // Espera 1 segundo antes de hacer la llamada a la API

    return () => clearTimeout(delayDebounce); // Cancela la ejecución si cambia el estado antes del tiempo
  }, [
    newPresupuesto.Units,
    newPresupuesto.PVP,
    newPresupuesto.CustomerTarget,
    newPresupuesto.MarkUp,
    newPresupuesto.Id_Incoterm,
    newPresupuesto.TipoPrenda,
    newPresupuesto.ABREVIATURA,
    newPresupuesto.VOL,
    newPresupuesto.IdSubgrupo,
    localNotas,
    productosYMuestras,
    modelosTipo,
  ]);

  const resolveClienteNombre = () => {
    const id =
      selectedCliente || newPresupuesto?.IdCliente || budget?.IdCliente || "";
    const fromList = (clientes || []).find(
      (c) => String(c.id) === String(id),
    )?.name;
    return (
      fromList || newPresupuesto?.ClienteNombre || budget?.ClienteNombre || ""
    );
  };

  const isCurrentTommyClient = () =>
    isTommyClient({
      idCliente: selectedCliente || newPresupuesto?.IdCliente,
      clienteNombre: resolveClienteNombre(),
    });

  const handleStateChangeClick = (newState) => {
    if (newState === "Offered") {
      if (isCurrentTommyClient()) {
        setTommyOfferedOpen(true);
        return;
      }
      setOfertarChoiceOpen(true);
      return;
    }
    setConfirmStateChange(newState);
  };

  const handleOfertarPrint = async () => {
    const id = newPresupuesto?.IdPresupuesto;
    if (!id || ofertarPrintBusy) return;
    setOfertarPrintBusy(true);
    try {
      await printBudgetPdf({
        idPresupuesto: id,
        nombreProducto:
          newPresupuesto.DiseñoMuestra ||
          newPresupuesto.designCliente ||
          newPresupuesto.NombrePresupuesto,
        unidades: newPresupuesto.Units,
        proveedorNombre: newPresupuesto.ProveedorNombre,
        precioUnitario: calculateSellingPriceUN(),
        totalVenta: calculateTotalSellingPrice(),
        moneda:
          (Array.isArray(lines) &&
            lines.find((l) => l?.MonedaVenta)?.MonedaVenta) ||
          "USD",
        fechaPresupuesto:
          newPresupuesto.FechaPresentacion ||
          newPresupuesto.FechaAceptacion ||
          new Date(),
      });
      if (String(newPresupuesto?.Estado || "") !== "Offered") {
        await handleStateChange("Offered");
      }
      setOfertarChoiceOpen(false);
      if (typeof setRefreshKey === "function") {
        setRefreshKey((k) => k + 1);
      }
    } catch (err) {
      console.error("Error al imprimir y ofertar:", err);
      alert(
        err?.message ||
          t("EstimatesPage.drawer.errorChangeState") ||
          "No se pudo imprimir el presupuesto",
      );
    } finally {
      setOfertarPrintBusy(false);
    }
  };

  const handleStateChange = async (newState) => {
    try {
      // Fecha actual en formato ISO
      const fechaActualISO = new Date().toISOString();

      // Definir qué fechas deben actualizarse según el estado
      let fechasActualizadas = {
        FechaPresentacion: null,
        FechaAceptacion: null,
        FechaCancelacion: null,
      };

      // Asignar la fecha correspondiente según el estado
      if (newState === "Offered") {
        fechasActualizadas.FechaPresentacion = fechaActualISO;
      } else if (newState === "Cancelled") {
        fechasActualizadas.FechaCancelacion = fechaActualISO;
      } else if (newState === "Accepted") {
        fechasActualizadas.FechaAceptacion = fechaActualISO;
      }

      // Si el estado es "Created", resetear todas las fechas con el nuevo endpoint
      if (newState === "Created") {
        await axios.put(
          `${API_URL}/api/presupuestos/${newPresupuesto.IdPresupuesto}/estado`,
          { estado: "Created" },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        await axios.put(
          `${API_URL}/api/presupuestos/${newPresupuesto.IdPresupuesto}/reset-fechas`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        setNewPresupuesto((prev) => ({
          ...prev,
          Estado: "Created",
          FechaPresentacion: null,
          FechaAceptacion: null,
          FechaCancelacion: null,
        }));
      } else {
        // Si no es "Created", actualizar normalmente
        await axios.put(
          `${API_URL}/api/presupuestos/${newPresupuesto.IdPresupuesto}/estado`,
          {
            estado: newState,
            FechaPresentacion: fechasActualizadas.FechaPresentacion,
            FechaAceptacion: fechasActualizadas.FechaAceptacion,
            FechaCancelacion: fechasActualizadas.FechaCancelacion,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        setNewPresupuesto((prev) => ({
          ...prev,
          Estado: newState,
          FechaPresentacion: fechasActualizadas.FechaPresentacion
            ? formatDateForDisplay(fechasActualizadas.FechaPresentacion)
            : prev.FechaPresentacion,
          FechaAceptacion: fechasActualizadas.FechaAceptacion
            ? formatDateForDisplay(fechasActualizadas.FechaAceptacion)
            : prev.FechaAceptacion,
          FechaCancelacion: fechasActualizadas.FechaCancelacion
            ? formatDateForDisplay(fechasActualizadas.FechaCancelacion)
            : prev.FechaCancelacion,
        }));
      }
    } catch (error) {
      console.error("Error al cambiar el estado del presupuesto:", error);
      alert(t("EstimatesPage.drawer.errorChangeState"));
    }
  };
  // Al guardar o actualizar los datos
  const handleSavePresupuesto = async () => {
    // Bloquea si faltan campos
    if (!canCreate) {
      alert(
        t("EstimatesPage.drawer.errorMissingFields", {
          fields: missingKeys.map((k) => labelByKey[k]).join(", "),
        }),
      );
      return;
    }

    try {
      const selectedItem =
        findCatalogItem(productosYMuestras, {
          idProducto: newPresupuesto.IdProducto,
          idMuestra: newPresupuesto.IdMuestra,
          nombre: newPresupuesto.DiseñoMuestra,
        }) ||
        productosYMuestras.find(
          (item) =>
            String(item.Nombre ?? item.nombre ?? "")
              .trim()
              .toLowerCase() ===
            String(newPresupuesto.DiseñoMuestra ?? "")
              .trim()
              .toLowerCase(),
        );

      let identity = getProductIdentityFields(selectedItem, modelosTipo);

      if (!hasPresupuestoProductIdentity(identity)) {
        try {
          const fromApi = await fetchProductIdentity({
            nombre: newPresupuesto.DiseñoMuestra,
            idProducto: identity.IdProducto ?? newPresupuesto.IdProducto,
            idMuestra: identity.IdMuestra ?? newPresupuesto.IdMuestra,
          });
          identity = { ...identity, ...fromApi };
        } catch (error) {
          console.error(
            "No se pudo resolver identity antes de guardar:",
            error,
          );
        }
      }

      const supply = getProductSupplyFields(selectedItem, proveedores);

      const formattedPresupuesto = {
        ...newPresupuesto,
        FechaPresentacion: formatDateForAPI(newPresupuesto.FechaPresentacion),
        FechaAceptacion: formatDateForAPI(newPresupuesto.FechaAceptacion),
        FechaCrear: formatDateForAPI(newPresupuesto.FechaCrear),
        FechaCancelacion: formatDateForAPI(newPresupuesto.FechaCancelacion),
        FechaMaximaContestacion: formatMuestraDateForAPI(
          newPresupuesto.FechaMaximaContestacion,
        ),
        MarkUp: newPresupuesto.MarkUp || null,
        CustomerTarget: newPresupuesto.CustomerTarget || null,
        PVP: newPresupuesto.PVP || null,
        IdSubgrupo: newPresupuesto.IdSubgrupo || supply.IdSubgrupo || null,
        TemporadaId: newPresupuesto.TemporadaId || supply.TemporadaId || null,
        IdProveedor: newPresupuesto.IdProveedor || supply.IdProveedor || null,
        IdProducto: identity.IdProducto ?? newPresupuesto.IdProducto ?? null,
        IdMuestra: identity.IdMuestra ?? newPresupuesto.IdMuestra ?? null,
        TipoPrenda: identity.TipoPrenda || null,
        ABREVIATURA: identity.ABREVIATURA || null,
        VOL: identity.VOL ?? null,
      };

      if (newPresupuesto.IdPresupuesto) {
        await axios.put(
          `${API_URL}/api/proyectos/${newPresupuesto.ProyectoId}/presupuestos/${newPresupuesto.IdPresupuesto}`,
          formattedPresupuesto,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
      } else {
        const response = await addPresupuesto(
          newPresupuesto.ProyectoId,
          formattedPresupuesto,
        );
        setNewPresupuesto((prev) =>
          applyIdentityToPresupuesto(
            {
              ...prev,
              IdPresupuesto: response.IdPresupuesto,
              IdProducto: response.IdProducto ?? prev.IdProducto ?? null,
              IdMuestra: response.IdMuestra ?? prev.IdMuestra ?? null,
            },
            response,
          ),
        );
      }

      onClose();
    } catch (error) {
      console.error("Error al guardar el presupuesto:", error);
      alert(error?.message || t("EstimatesPage.drawer.errorSave"));
    }
  };

  const handleDeleteBudget = async () => {
    try {
      await axios.delete(
        /*  `${API_URL}/api/proyectos/${newPresupuesto.ProyectoId}/presupuestos/${budget.IdPresupuesto}`, */
        `${API_URL}/api/proyectos/${newPresupuesto.ProyectoId}/presupuestos/${newPresupuesto.IdPresupuesto}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      setConfirmDeleteOpen(false);
      onClose();
    } catch (error) {
      console.error("Error al eliminar el presupuesto:", error);
    }
  };

  // ===== COSTE UNITARIO =====
  const calculateCostPriceUN = () => {
    const units = n(newPresupuesto.Units);
    if (!units) return 0;
    return calculateTotalCost() / units;
  };

  // ===== VENTA UNITARIA =====
  const calculateSellingPriceUN = () => {
    const units = n(newPresupuesto.Units);
    if (!units) return 0;
    return calculateTotalSellingPrice() / units;
  };

  // ===== MARGEN UNITARIO =====
  const calculateMarginUn = () => {
    const units = n(newPresupuesto.Units);
    if (!units) return 0;
    return calculateTotalMargin() / units;
  };

  // coste por UNIDAD con máxima precisión (sin redondeos de UI)
  const unitCostExact = (line) => {
    const base = n(line.Coste) * n(line.Un) * (n(line.TipoCambio) || 1);
    return PERCENT_IDS.has(Number(line.ESCANDALLO_SUBGRUPO))
      ? base / 100
      : base;
  };

  // venta por UNIDAD calculada desde el coste exacto y el margen de la línea
  const unitSaleExact = (line) => {
    const c = unitCostExact(line);
    const m = n(line.MargenVenta ?? 10);
    const denom = 1 - m / 100;
    return denom !== 0 ? c / denom : n(line.Venta) || 0;
  };

  // ===== COSTE TOTAL =====
  const calculateTotalCost = () => {
    const units = n(newPresupuesto.Units);
    const isDDP = Number(newPresupuesto.Id_Incoterm) === 4;

    const total = lines.reduce((acc, line) => {
      const isImport = line.ESCANDALLO_GRUPOS === IMPORT_GROUP_ID;
      const sg = Number(line.ESCANDALLO_SUBGRUPO);
      const cUnit = unitCostExact(line);

      if (!isDDP) {
        // no DDP: import se suma 1 vez; resto × Units
        return acc + (isImport ? cUnit : cUnit * units);
      }

      // DDP
      if (!isImport) return acc + cUnit * units; // proveedor × Units
      if (PER_SHIPMENT_IMPORT_IDS.has(sg)) return acc + cUnit; // por envío (1 vez)
      return acc + cUnit * units; // resto de import × Units
    }, 0);

    return Number(total.toFixed(2));
  };

  // ===== VENTA TOTAL =====
  const calculateTotalSellingPrice = () => {
    const units = n(newPresupuesto.Units);
    const isDDP = Number(newPresupuesto.Id_Incoterm) === 4;

    const total = lines.reduce((acc, line) => {
      const isImport = line.ESCANDALLO_GRUPOS === IMPORT_GROUP_ID;
      const sg = Number(line.ESCANDALLO_SUBGRUPO);
      const sUnit = unitSaleExact(line);

      if (!isDDP) {
        return acc + (isImport ? sUnit : sUnit * units);
      }

      if (!isImport) return acc + sUnit * units;
      if (PER_SHIPMENT_IMPORT_IDS.has(sg)) return acc + sUnit; // 1 vez
      return acc + sUnit * units;
    }, 0);

    return Number(total.toFixed(2));
  };

  // ===== MARGEN TOTAL =====
  const calculateTotalMargin = () => {
    const units = n(newPresupuesto.Units);
    const isDDP = Number(newPresupuesto.Id_Incoterm) === 4;

    const total = lines.reduce((acc, line) => {
      const isImport = line.ESCANDALLO_GRUPOS === IMPORT_GROUP_ID;
      const sg = Number(line.ESCANDALLO_SUBGRUPO);
      const cUnit = unitCostExact(line);
      const sUnit = unitSaleExact(line);
      const delta = sUnit - cUnit;

      if (!isDDP) {
        return acc + (isImport ? delta : delta * units);
      }

      if (!isImport) return acc + delta * units;
      if (PER_SHIPMENT_IMPORT_IDS.has(sg)) return acc + delta; // 1 vez
      return acc + delta * units;
    }, 0);

    return Number(total.toFixed(2));
  };

  // Formatea números con separador de miles y 2 decimales (locale ES)
  const formatNumber = (value, decimals = 2) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "";
    return num.toLocaleString("es-ES", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  // % de margen sobre la venta total: (TotalMargin / TotalSellingPrice) * 100
  const calculateMarginPercentage = () => {
    const totalSale = calculateTotalSellingPrice();
    if (!totalSale) return 0;
    const totalMargin = calculateTotalMargin();
    return Number(((totalMargin / totalSale) * 100).toFixed(2));
  };

  /* FOTOS */

  /*  const [shouldSyncFiles, setShouldSyncFiles] = useState(true); */
  const syncTimeoutRef = useRef(null);
  const fetchAbortRef = useRef(null);

  useEffect(() => {
    // sincroniza SOLO si el Drawer está abierto y hay número de presupuesto
    if (!open || !budget?.NumeroPresupuesto) {
      setUploadedFiles([]);
      return;
    }

    const controller = new AbortController();
    fetchAbortRef.current = controller;
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const files = await getFileContent(budget.NumeroPresupuesto, {
          signal: controller.signal,
        });
        if (cancelled) return;
        setUploadedFiles(files);
        // reintento con backoff simple si viene vacío
        if (files.length === 0) {
          syncTimeoutRef.current = setTimeout(fetchOnce, 3000);
        }
      } catch (err) {
        // si se canceló, no loguear como error funcional
        if (err?.name === "CanceledError") return;
        if (axios.isCancel?.(err)) return;
        console.error("❌ Error al sincronizar archivos:", err);
      }
    };

    fetchOnce();

    // cleanup: al cerrar el Drawer o cambiar el número, se cancelan TODOs
    return () => {
      cancelled = true;
      controller.abort();
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
    };
  }, [open, budget?.NumeroPresupuesto]);

  const handleFileUpload = async (file, IdPresupuesto) => {
    if (!file || !IdPresupuesto) {
      alert("Archivo o IdPresupuesto no válido.");
      return;
    }

    try {
      // Subir archivo
      const response = await uploadProjectFile(file, IdPresupuesto);
      if (!response || !response.filePath) {
        throw new Error(
          "Error en la subida del archivo. La API no devolvió un archivo válido.",
        );
      }

      // 🔹 Actualiza la UI de inmediato para una respuesta instantánea
      setUploadedFiles((prev) => [
        ...prev,
        {
          fileName: file.name,
          filePath: response.filePath,
          mimeType: file.type,
          data: null, // todavía no tenemos el base64
        },
      ]);

      // 🔹 Espera un instante a que el backend procese el archivo
      await new Promise((r) => setTimeout(r, 1500));

      // 🔹 Refresca los archivos de este presupuesto automáticamente
      if (budget?.NumeroPresupuesto) {
        const updatedFiles = await getFileContent(budget.NumeroPresupuesto);
        if (updatedFiles.length > 0) {
          setUploadedFiles(updatedFiles);
        }
      }

      console.log("✅ Archivo subido y refrescado automáticamente");
    } catch (error) {
      console.error("❌ Error al subir el archivo:", error);
      alert("Error al subir archivo. Intente de nuevo.");
    }
  };

  const retryGetFileContent = async (IdPresupuesto, retries = 5) => {
    if (!open || !IdPresupuesto || !budget?.NumeroPresupuesto) return;
    const numero = budget.NumeroPresupuesto; // fija el número para esta ronda

    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    await wait(3000);
    for (let i = 0; i < retries; i++) {
      // si el Drawer se cerró o cambió el presupuesto, aborta
      if (!open || budget?.NumeroPresupuesto !== numero) return;
      try {
        const updated = await getFileContent(numero, {
          signal: controller.signal,
        });
        if (updated.length > 0) {
          setUploadedFiles(updated);
          return;
        }
      } catch (e) {
        if (e?.name === "CanceledError" || axios.isCancel?.(e)) return;
        // silencio: reintenta
      }
      await wait(3000);
    }
    console.error("❌ No se encontraron archivos después de varios intentos.");
  };

  /* FOTOS */

  /* diseño cliente */

  const [localDesignClient, setLocalDesignClient] = useState("");

  useEffect(() => {
    if (budget) {
      setLocalDesignClient(budget?.designCliente || ""); // Sincroniza las notas locales con las iniciales del presupuesto
    }
  }, [budget]);

  // Sincroniza las notas locales con las iniciales del presupuesto

  useEffect(() => {
    if (budget) {
      setLocalNotas(budget?.Notas || "");
    }
  }, [budget]);

  const handleSaveNotas = async () => {
    if (!newPresupuesto.IdPresupuesto) return;

    try {
      await axios.put(
        `${API_URL}/api/proyectos/${newPresupuesto.ProyectoId}/presupuestos/${newPresupuesto.IdPresupuesto}`,
        { Notas: localNotas }, // ✅ Guardar `localNotas` en la API
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      setNewPresupuesto((prev) => ({
        ...prev,
        Notas: localNotas, // ✅ Asegurar que `newPresupuesto.Notas` se actualiza con `localNotas`
      }));
    } catch (error) {
      console.error("Error al guardar las notas:", error);
      alert("Error al guardar las notas.");
    }
  };

  /* NOTAS */
  const handleFileDelete = async (fileName) => {
    const numeroPresupuesto = budget?.NumeroPresupuesto; // Verifica que exista

    if (!numeroPresupuesto) {
      console.error("NumeroPresupuesto no definido");
      return;
    }

    // Construir ruta relativa sin duplicados
    const filePath = `Proyectos/${numeroPresupuesto}/${fileName}`;

    try {
      await deleteFile(filePath);
      setUploadedFiles((prev) =>
        prev.filter((file) => file.fileName !== fileName),
      );
    } catch (error) {
      console.error("Error al eliminar archivo:", error);
    }
  };

  const [inputValue, setInputValue] = useState("");

  const openDuplicateDialog = () => {
    setDuplicateFechaMaxima(
      formatMuestraDateForAPI(newPresupuesto?.FechaMaximaContestacion) || "",
    );
    setDuplicateFechaError(false);
    setConfirmDuplicateOpen(true);
  };

  const handleDuplicateBudget = async () => {
    if (!newPresupuesto.IdPresupuesto || duplicateBusy) return;

    const fechaMaxima =
      formatMuestraDateForAPI(duplicateFechaMaxima) ||
      formatMuestraDateForAPI(newPresupuesto.FechaMaximaContestacion);

    if (!fechaMaxima) {
      setDuplicateFechaError(true);
      return;
    }

    setDuplicateBusy(true);
    setDuplicateFechaError(false);
    try {
      const responseLines = await axios.get(
        `${API_URL}/api/presupuestos/${newPresupuesto.IdPresupuesto}/lineas`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      const originalLines = responseLines.data || [];

      const duplicatedBudget = {
        ...newPresupuesto,
        IdPresupuesto: null,
        NombrePresupuesto: buildCopyPresupuestoNombre(
          newPresupuesto.NombrePresupuesto,
        ),
        Estado: "Created",
        FechaCrear: new Date().toISOString(),
        FechaPresentacion: null,
        FechaAceptacion: null,
        FechaCancelacion: null,
        FechaMaximaContestacion: fechaMaxima,
      };

      const responseBudget = await addPresupuesto(
        duplicatedBudget.ProyectoId,
        duplicatedBudget,
      );

      const newBudgetId = responseBudget.IdPresupuesto;

      const duplicatedLines = originalLines.map((line) => ({
        ...line,
        IdLineaPresupuestos: null,
        IdPresupuesto: newBudgetId,
      }));

      for (const line of duplicatedLines) {
        await createBudgetLine(newBudgetId, line);
      }

      const nextPresupuesto = {
        ...duplicatedBudget,
        IdPresupuesto: newBudgetId,
        Estado: "Created",
        NumeroPresupuesto:
          responseBudget?.NumeroPresupuesto ||
          duplicatedBudget.NumeroPresupuesto,
      };

      setNewPresupuesto(nextPresupuesto);
      setLines(duplicatedLines);
      setConfirmDuplicateOpen(false);
      notifyContestacionAvisosChanged();

      const focusBudget = {
        ...(budget || {}),
        ...nextPresupuesto,
        IdPresupuesto: newBudgetId,
        id: newBudgetId,
        IdProyectoCab:
          nextPresupuesto.ProyectoId ||
          budget?.IdProyectoCab ||
          budget?.ProyectoId,
        PresupuestoEstado: "Created",
        FechaPresentacion: null,
        FechaAceptacion: null,
        FechaCancelacion: null,
        FechaMaximaContestacion: fechaMaxima,
      };

      if (typeof onBudgetAdded === "function") {
        await onBudgetAdded(focusBudget);
      }

      setDuplicateSuccessNombre(nextPresupuesto.NombrePresupuesto || "");
      setDuplicateSuccessOpen(true);
    } catch (error) {
      console.error("Error al duplicar el presupuesto y sus líneas:", error);
      alert(
        error?.response?.data?.error ||
          error?.message ||
          "Error al duplicar el presupuesto y sus líneas.",
      );
    } finally {
      setDuplicateBusy(false);
    }
  };

  const drawerActionIconSx = {
    color: "primary.main",
    border: "1px solid",
    borderColor: alpha(theme.palette.primary.main, 0.35),
    backgroundColor: alpha(theme.palette.background.paper, 0.92),
    width: { xs: 36, sm: 40 },
    height: { xs: 36, sm: 40 },
    flexShrink: 0,
    "& .MuiSvgIcon-root": {
      fontSize: { xs: "1.05rem", sm: "1.15rem" },
    },
    "&:hover": {
      backgroundColor: "primary.main",
      color: "primary.contrastText",
    },
  };

  // Logo Claro
  const miLogoBlanco =
    "iVBORw0KGgoAAAANSUhEUgAAARAAAAEQCAYAAAB4CisVAAAACXBIWXMAAC4jAAAuIwF4pT92AAAcoUlEQVR4nO2d4XXbONeEB3vyf50KXrqCOBWsUkGSCqJUEG8F663ASQVyKrC3AnErsFOBuBXYXwXz/QBoQRQpURRJAOQ85/iYtCgJlsjh3IsLwEDMBpILb3dRefh3AFctXuYFwK/K3wr3AwCFMaaAmAUmdANEP5C8gBWAzP38z9vOwrQKufv9BOD/3P6LMeYpUHtEz0hAEoTkFaxYvHO/rwBcBG3U6TzBupZfsMIi55IgEpDIIZnBCsQf7vei40vl7nc1BCmwDT9OxRcuPwTK0M31vMC28xeA3BiTd2yXGAkJSGQ4wVjACsYC7S/Ewv386/ZzBA4XvLCq/F2GVYsTXiaH/Z8kKBEiAYkAkp+wFYw2icwcNgT4BWv986HaNhROKDPY/7kMxbIWT32AFZQHhTzhkYAEwN2ZPwH4CHsBHcpfFNi19ZNNQLrPZYFtyLY48pQCVlB+TvlzEQIkL0guSd7zMM8kV+7YLHS7Q0NyQfKG5PrI57YheesSzEJMA5KfnCAc4tFdJDr5D+BEuPw8N0fE5EYCLJKEZObuhodO8nvKZZwFyasWn/Oa5DJ0W4U4ihOE9bGTmTbWFz3SQkye3eNZ6LYK8Qqtrb45cOJuSF7rxB0P2rzJyolGk5AvQrdTzBjaMKXpJC0TocppBITbxPXjAXFfhm6nmBGecBxyGwpRIoM2xDn0vd3oexODcUQ41rTFYCJyeDjkfJaQiF45IhwrKkxJFtrwRkIi+qeFcGSh2yj64YCQbKgciTgFz+LWJUclHBPmiJAsQrdPRI47gSQcM+eAkKx1Hog9aDP064YTRjmOmXLghqL8iHgNV25lWUUT3Ia0deeIet7mCu2grOrd5Znkdei2ifigTarXjaS+V1gzI9wdpe5EWMmWimPQlslvdOOZIQ2uQ+GKOIkDYc1abmSCHHAdN6HbJtKFNvleHWfzTNWOTIcGy/lI9a6InmhwI/cKiROn4Yu9Cd0uMT0a3MhGN6oEoc2Yr2u+zEXotolp03DTUoI1FVzIUk2Uyk6K0WgIm3UOxk6N+iuhJYLA+sS9QpoYcV/WqvJlKVEqgkM7wZRuarFCm++oJq9kF0U0NITVN6HbNXtoM9/VL0YJKxEdziVXb3Qr3egCwf2Rks/U4CYROQ2htkRkTJx4+GyU7xCpUJMXUb5uLKTgYgo0OGiJyJDUiIdiSJEs3M/hSUSGok48QrdJiHNxIrKpnNvL0O2aDKwvyLkJ3S4h+oL1PTTL0O1KHn2wYi7oXO8ZfaBibuic7xHuj6Zdhm6TSAvaGejumVBxoUSkB7ifMF2GbpNIA9qhDbfcr1BeM5HpBiUiZyDxEKfiLrhlzUVXJZnJjxtERF28h5B4iFOgHaRWPWfasGYCbqRGRFQn0gT3y3uXodsk4oM2RLlh/dKTp5CEG5GItID7Y1tUJCZ2cOfI+kzRqGPNyN1IjYho+EYJrQ2VeIg9aKs0V6xfn7ZPoncj3C97l4jUfCjr0G0SYaG9217z/BClC2tG7Ea4f73M92ZL2TLhwW3NRmiidiO0n5PPTeg2BYG78ewzJR6zg801GzGwZqRuhPs5w2XoNo0K7UlToqzyjGD7mo0YiNaNcLf7ej7XEOeunjOF3Ws2YmDNCN0Id0V4w6m7eO4ngW5Dt0kMB/ur2YiB6NwIrZvzr6f70G0aDO4nTdXjMlFoXWYMCdEhiGrJENqbsk9UItcb3I/ZovkSxPlwvJqNGIhqBQDuV3FPKx/C/a6nReg2ifPhtmYjhYToEETjRrjr+DaxtOtsaONg/650E7pN4jwYT81GDEThRmjFfOO1axr5EO7WeyjvkSjc1mz4J6nYEtyNcD8fElzYzoI2A1/yzAi7wkQzTKtmIwaCuxFO5Zrj1NRwRnBbszGHhOgQBHUjnILr5+5daxrx2IThtGo2YiCYG+F+3jGtrl3u26hpZIQnCKddsxEDQdwId7t207kGua9+Cl0ig/Oq2YiBIG6Eu6FMGlFAko2eAVTNRgyM6kaY2s2cuwVj6dimCUPVbMTGqG6Eu+mEDWO9JrlfyJJW4mZCUDUbKTCaG+Gu67zp87VNXy/kGvaX230yxrzv67XFcdzJ+AnAFwCLsK0RLXkB8NkYkw/5JrRDR/zu3EtjTDHke54E7R3PZxG6TXOBqtmYArcc2I1wdzBrb7nJXhyIa1AZ190ZY7728bqiHtrqwk8AvgHIgjZG9EUB4OtQbsQJ1AZAKVQf+nivswWEu/boBdYevZz7umIf2tnbPmIr1mJ6fAfw9xDXEAdIM/QhIGtsY+6/jTE3576m2EI7t8MXAEts7x5i2hQYyI2Q3GDrWr8aY+7Oeb2zBMTdEcu1KQpjzOU5rycszm4uYYVjWpPDiFPo3Y30fc2eKyC9qtncoa0P+AgrHkIAA7iRStRw1nXbWUDkPvrBJUS/weY1sqCNETHTmxvpM2/52xnt+Mvb/vuM15kd3M6zsYbNjF9D4iEOcw3gkT2USDg3k7vdC/fa3V6ry5Mq7iM3xnzo2oA54b78L7BuQwlR0ZWz3UjVhRhj3nZ5na4C4uc+eulPniqq2RADUeDM3EgfuZCTBUS5j3a4hGjpNoQYis5upOJCOl3LXXIgX7xt5T48aOfZuCX5DMCvzhViKDrnRiq5kIwdlpk9yYH0oVhTg9tBbN+gmg0Rlu/GmD9PecK51/SpDuSbtz1r90E7z8YKwDNsSCfxEKEpJ4xqfS46F1K43exUJ9Pagbhk4MbtznLMi/sMlrBhXBayLUIcofWwkkpe88EY87ntm5wiILfY9hfPZswLNc+GSJcn2N6Vp2MHurxdWVrQer6QU0KYpbd9d8LzksQlRFewrmsFiYdIjyvYBOtNi2N/eNvfGo+q0MqBnGNxUsIbxKaaDTE1DrqRaoqibWFZWwfid93+aDwqUTy38QzgFhIPMT0OuhEXsjy43Yu2XbpHHUhFmSbVdeuy1bdQeCLmRe1kQq74sZzusFWk0caB+PHQz3btixs3mG0F4BESDzEfCgB/Aqgdu2aMecC2S/cTWyzM3UZA/GrKuxbHRw3tchMbaM4NMQ9eYK/b98aYS2PM9yPlFw/e9tFK6oMhTKVKLenkqVNT9aaIuZAD+HnqALlTUxbHHIifPP3nlIbEhIvtFK6IqVPAVohfGmM+dBpda5OpZU9Ndqyq9ZiA+BbmofGoiHFZ53to/g0xXe5gF6i6NMbc9LBolJ/r/NJ4FA4IiLtrlxfdXYpl6y5R+tfRA4VIjycAXwG8NcZ8dQnQvmidB3lz4LGP3nZS4YsrCFtDA9zEtCgToj/blKd3xRhTkHyCvX4ykldN73cohPGVJ++xfYMi8RAT5AE2RHlrjPlzSPHw8MOYRhdSKyAucVKGLw+phC8SDzEhCtiajUtjzOeeQ5Q2+O/3semgphAm1d6XW0g8RLq8wF64P0PPM1wJY65IZnXJ2aYQZuFtJ9H74qYbWIZuhxAdyGETopcuIZqHbc4rvnlY1B2wJyCukKS8iz+lEL64gT+d17YQIgAF7ITIrzUbEV5rR8OYuhBm4W1HH744wbsN3Q4hWnIH4J8AOY2TMcY8kXyBzYcu6o6pC2H+8Laj/yehIjERP0+wCdEhajaGxh/iv6g+WCcgZZfNy0jdRZ1xVaZKmooYeYENUd4bY963GMQWK/9624vqgzshTKX7Nh+sST3gLUotREw8wIYod6Eb0hO5t/1H9cFqDsS/m/+LuFlBoYuIgwK28Oquh3EoUeG6cwvYWfoW1cerIYyvMPlQjToXF4stAjdDzJuyrPxDj4PYYiUvN6p5kKqAvD4Yef5DA+REKMpBbLHVbAyJH43s5BxfBcSVgWduNx+8SR2R+xABKLCt2Xgfac3GkPhmYicP4udAUsl/yH2IsSjLylPqdu2dSj1IvQPB7l09yvDF9bwsAjdDTJsC25qNEIPYYsWfpey188IXkHc1B8eGum3FEJw68fAeJDOSN25M1hSpzYPUhTAvEWeTl6EbICZFjg4TD/u4cVg76ya7PN3niK+jLvimYgGXJ/UFJKs5MBoqUywK0ZUCZ9ZsuILLb7BV23XnZLkK3IfIezNPofC2/1duvAH2+nZjTaDuVcEJcQJ3OGOejQ7rJl/AisjXKVSlukRquZuVG2+qf8Cu0sTE0UVuhKjwBLuWc+dZ9Zzz/YLu59+KJKYgIrBhywJeuJaEgLjelyxwM0QalAnRH2eEKBms01iin7B5KiLyKsLlDGWlgPjhQYwx2yJ0A0T0nFWz4UKUT7DCMcQI7ymIyC9snVgG4FVASpV9ibTC7t3xQ8QMKbANUYouL+Dyf2WIMnSSfkXyJeHaEt9cXAHI33g71QNiQnN+iJJy4uEfXXs4XIiyhBWOrK+GtWRFski0d8Y3FxcA8MavKqscEBOL0A0Qwclhu1/PSYguYef2DJmQvwBwT/J9pG6/EWNM7vXEvANsEtW/u/8au1HHqAicmBcFhq/ZCEEGOxXnh8DtOAfrQCp/jFERFb7MjzucMfGwV7PxBfGePwuS18aY76EbciKva8UAVkAWlQeFCMETtm4jVM3G2PxFsnMCOBDld1PrQGIkC90AMRhlzUbnxaK9mo1PSO9cuYCdmjOlUKYoN0hevAHwu/dgjA4kC90A0TtnTTw8Qs3GmCxIfkqoa/c/b/tqJ4maWlZYJEWBtGo2xuQWaazBtEcKIYxIl7Jm45xBbBm2biPrq2GRkZFcJlKlWvg7b5oeEOIMckyjZmNM/oLNB8VO4W0v3mCr6kX1SCFOoMC2QrTo8gKuZuML+hvElhIpuZBXfkP8tjAP3QBxkAfY2bcujTF/nioeJC9IXpN8BPAI4BrzE4+S5KbsVA5EdKGvmo2P0DSVPlckryIfJ7PzfacgIOoZioO+BrGlWrMxFl8QZzkFgL2ZyeIXkGqDxej0VbOxM/GwaGQJu6xECvzuC0isc6ECNkGXBW7DnChw/iC2BaZZszE0FwmEMSVX0TsQxxMkIGNwB9VsxMAnRBzG+KQiIP5UaqJfYph4WOySzAoEqQhIDq2J2yd9TDw855qNoVmEbkBbkhCQykxIojuxTzwsHCQXXUPJMUlCQBw5ElLmiChgQxTVbKTFFRIoovQF5PfGo+LgH0hA2pL6xMPCWz4yYgpfQGK3pA+ww55FMznOWCxaNRtREfv1CAD/JRPCGGMKkjl0YlcpMM2Jh0UCJCMgjp+QgJTc4byJhzOoZiNmFqEbUIcrEHwlKQExxtyRvMV875Kq2RBR8RsSyPRW+BG6ASPzAuA7gPfGmPfGmJN7U0hmJG9JPsOuRyLxEL2QUhK15Dus7Z66C1HNhoid3BeQJC5IY8wLyR+YZmVqgf4mHl721SghPHZuRm+Q5lSGU3IhqtkQKbFzzb2Bt84DySyFVbKcC/kbadeF5Ohn4mHVbIhQPFV7YTIk4kiMMd9Jxrz2aR0F+pl4WDUbIhTvyg1jzMsbJDLvQANfYSfijZ079LNYtGo25kMeugENlDetF8B24/r2eTF2a87B5Qxinf6tbNtbY8zXLuJB8hPJewDPsOFa1m8ThTiZzP1+AtJNor7iQpmPiEP8+lwsegmFKHMm1sgg83feuDEm5X4yMyFV+AwbymSB3l+LRYu++e/4IePibm4l/wLbQrICCdtj1yvzGcAa4921C2jiYTEcMTqQrPqHqoAsRmtKz7jlH0oRGYq+FoteQjUb4jCxC0gObAXkCU48UqkFqcNNffgVwKrnl+5jENsS81osWnTnqet5NjCZt/0CbAXk/yoHFaM0ZwDciF3gfBEpoJoNEYY8dAMaeM2Rlp0Ev7n93Dso+SSeS2Z+RrdlMfteLHoJiYc4jVgXeSvP49fwys+BlKQwF+NRjDEPJAu0S6z2tVi05tkQfZCHbkADpbkoyj+8AV6nC3yBvdCSdyAlLrF6CTsHxqLysBaLFjHSOc82JJWZyH6VG795fywvIv/A5DHGvBhjPgD42/0pB/DVGFNWiJ4kHi5EWboQZQPgGhIP0R//hG5AA5m3nZcbptxwUwVeu933iSzuOxqq2RAj8GKMeRu6EXVU9OGyzA36o3F/edtXiLMfelQ08bAYmZin6yxTGy9+x4IvILm3/Q4zRjUbIhB3oRtwgIX7nft/fBWQSiJ1gZmhxaJFYDoPiRiapgQqsL+sQw57151MT8whvHk2UpuYSEyPv48fEgz/2sj9B37bPW6rLtUFZKaEm2djhe08GxIPEZLvsboPh1+BmvsPVAXEf3AxWHMC4K2NsoGtC1kGbpIQgK1Hitl9AFst2OtY2Qlh3GC0cjfVuUFe0WLRIgH+jLFwrMTlBsucYF59vG5pyxz2YlsM1KbBUc2GSIS86yRUI7LwtvfG6FRDGMCrhHPjO5LAhSjXLkRZQ70pIm5eYCcFjx0//7E3r2+TA/Gf3Gkm8bHQIDaRKF8jT5yWlNdVXvfgngNxJexlTBblRUnySotFi4T53nWJjzGpRCC1Y3TqQhhg6zqyykSqwagMYnuErctXiCJS484YE+tSJFX8jpS87oAmAfHVJujdvVKzsYJqNkS6xLyOUR3ltV80Da6ty4EAu2rzBXYx69HQxMNigjwB+BBzl62P677N3G5juFUrIG6ZhAe4svYxJlpWzYaYMEmJh+OLt904R0lTCFN90uLc1jThEqIr2Ml5VkO+lxAByJGeeADb8OXl0BImhwTEty1fGo/qQKVmQxMPi6lyZ4xJTjzahi/AAQFx/3T55EUfvTHeYtEbaLFoMW3+NMakUChWR6vwBTjsQKpP7tQb4w1iU82GmAMF7JSgo3Y89Izf+9LNgTg6hTENEw8rRBFT5zsSn0/YjSPL3O7RYreDAuLCmDu3e+Vio4NvrpoNMUNyWOGIemRtS3yj8PPYwcccCLAbxnyrO8AJhz+ITYg5UMCOafmQsuso8UopALs+79H/6aiAuBiocLu1+QvXzfMD3ZaSFCI1nmCF4zKB4fin4E9/0WqG+DYOBNhamQs3Y/keLmn0HvEuyyfEOZTh/AdjzPuJCUdJGWH4PbAHMccPeS0t37jdJ2PM+yPHXwP4C0qcivR5gr0bR7nkZF+4/Oaj271r2wXdyoG4MvZSkY4mU+VGROIUsD0ql6XbmLJ4OPz8ZusFrlo5EOC1e2ftdlsrlNyISIgHAD9TmKujT1zy9Nnt5m4t6Va0zYGUidLC7S7bVqbKjYjIKWCH2L81xnyem3g4rr3to123Pq0FxOFPP79s+yRjTOFULfbp68U8KBOi711PyvcZhCiHKMOX4tTkcOsQpsSVpF/AfgmXp37wLn+iIjMRghw2RLkL3I5ocL2qK7f7tzHm5pTnn+pAgG2C5QK71qcVxpiyF0duRIxBAXuuXbqCr7uwzYmOv9zvF3SYOKyLA7mA7dLt7EK815IbEUNxB+s28sDtiJZz3QfQwYE4sfBdSOfRtXIjomeeYNdaeWuM+SrxOMpZ7gPo4ECAPRdSwCajzkpCyY2IjpQJ0R+JrLMSBX24D6BbDqTqQjJ0yIXUvKbciDiFBwCfjTFv3SjYInSDEuNs9wF0dCBAv7mQmteWGxF1FNiWlRdhm5IufbkPoKMDAWpzIWe7EO+15UZESV3NRhG2Senibvy9uA/gDAFxfMd2CP8317jecMr4HjY5JuZFDpsQvXQJUZ0D/XCN7YxjP86NGjqHMCVurMut2209RqbD+9xgq5ximhSwpdR3chn9M0Tnx9kCAgBuNrLM7Q42J6RyI5PlDsA/Mx2HMhpuutGl2/3aR1FdXwLyCXbGdeDE0Xwd3+8GciOpM4t5NmKhMt/H0Tl92tKLgAAAyTW2q8oNPqrRfSD30NoyKVEmRH8qpzEulevzQ3RFdm79l5LnvhOqDe954dacEXFzT+tSRQBol1gpWR1/RiBI3ngNvT3+jN7ed0FyM971IFqwoV2+NBvrPBD70N5kn913MsqNvTOusRvvJBot2Um5kRh4Jrminb1ORID7Pkp6q9UaDNr1b0sejz+j9/eXGxmfNa1NjvfuNkNor4WS0a/FztDGvCU3Ad5fbmR4NrSfcTb29yuOw4DRwNlwN+4K1njKjQzBikqIRg93b6A3odtzMgwcynjtkBs5n0fahKhClARgqqFLFQYOZSptkRs5jWda4U3H+oq0Q5cq3A9lFhG0R27kMPdsWL5UxA8jumn3AndDmQ0jsMGUG6myoWo2koe7BWPphi5VuHvXvz/+jOGh3IhqNiYEySvuFoxlodvUG7QX66N38kZT0ML5uZFHqmZjUnD/+lqGblPvcFchyYiSO+4LWI10AYdgQ9VsTBbunrvxjnU5F+7GaBtGdhekzdf4Ipc6GsQ2cVjJezCya6p3uKuW69DtqULrRvxMdmqoZmMm0Lr6kmdG5OoHhbvx2mijdk+BabmRMiE6jxNI1JVIzMdp1vzzy9BtqoPxuxHVbMwQ7idNb0K3aXS4a7/IiLsTGZcb2dDOu5KF/lxEGLibBoiiLCII3E0ARR3DMbwbWTFikRXjwN1Ju6afND0Gd4u5NrF/IBzXjahmQ7zC/Ruuzgtgz5JFr6oc1o2Ug9iy0P+niAfuDgmJ2q0HgbtJoSTq+NmvG1HNhqiF+0WYOk+qcD+znERFHc9zIxuqZkMcoEY8lqHbFC2pighwkhtRzYZohcSjAzUfWkoicsiNrHUCiLbUXAdRFlxGScoiAuy4kQ1VsyFOJPXzPwr0IYo5ovO+R/Rhijmh830A9KGKOaDzfEBqPtw11fUpJoLEYwTch7xTbCYREanD3fJ0Uj11w8H9OpFHqp5CJIrEIwA1IqJxASI5uDv+61niMTLcnwR5GbpNQhyD+8WGugGGgrtzI5Cq1hMRQzJTCB4ZNXHkmkquisigXX9IPYkxwv1usI2UXcRCjVNWN21ssD65ugzdLjFfavIdytXFDveTqytZRTE2zhVv5IoTxOVFFNKIINSELPe6iSUG9ytXyTmunyFGg7aXZV0556JZSF6ciItBbytf6Jqan0P0DO3UlHK9U4T70w0+684g+qDBdawUskwM1mfE5UZEZ2pcxzM1Y/q0qXEjyo2Ik6DNr1VdhxKlc6HBjWyopSPFAdx5U+1h2ch1zBTa8uJNzZ0kC902ERe0pQHVc+VWrmPmNNxV6P6mk2PmuJtMNVzRIDixC+uz6eqtmSnufFjVnA/L0G0TEdMQ1mx04syDBuGQIxWnwf1yeAnJhDkgHCsqJya6wG1+REIyUQ4Ix5rqlRN9cERIrmVt00PCIUbngJA803bpZaHbKA5DW0i4lnCIYHhCsqk5Ee+pwqKocN/XdcP3taK6ZEUoaJOt1WkD6E7WG7mScND2qK1qvhs5RhEXB07W0pUsqVzJ4NDmNprc4Ubfg4iaI3b52YmMQpwecaJx3eAE6T7zReh2CnESniupJl19MdEdsQMtRONRn62YBM6VLLk/Athn7S4IJfQaoO1BuWW9uyOVdxJTp6WYbDx3koVucyhoHdwN67tdfadxK+ENgwndgLlDmw/5CGABIGs4rADwBOBfAE/GmHyEpo2KE8orAH+434sDhz/AfhYPxphi6LaJZiQgEeHuogvYi2gB4FD8/uR+/gOQAyhSuZhoE5qZ+ykF49j/mgP41xjzMGzrxClIQCKmIihXaHYoPk8AXmDv0C9uH2O6FucmMlhRuALwO7btz1q8RI6t48qNMS/9t1L0gQQkITyb71v9U3sZXkXFUcC6mC68896/FItTyV0bfmGi4dmUkYAkDm03ZSkqF7DC0vViHopStAokGHKJZiQgE8YTF2A3KfmHt91VbAr34++XTqYMoyQSE+f/ARSKAQyyU4HqAAAAAElFTkSuQmCC";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleCloseDrawer}
      PaperProps={{
        sx: {
          width: { xs: "100vw", xl: "min(100vw, 1700px)" },
          maxWidth: "100vw",
          height: "100dvh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderTopLeftRadius: { xs: 0, sm: 20 },
          borderBottomLeftRadius: { xs: 0, sm: 20 },
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            ...pageHeroShellSx(theme, { sticky: false }),
            flexShrink: 0,
            mb: 0,
            borderRadius: 0,
            borderLeft: 0,
            borderRight: 0,
            borderTop: 0,
          }}
        >
          <Box sx={{ p: { xs: 1.25, sm: 1.5 } }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={1}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: 1 }}
                >
                  CRM · ESTIMATES
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.15,
                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                  }}
                >
                  {isEditMode
                    ? t("EstimatesPage.editBudget", {
                        defaultValue: "Edit estimate",
                      })
                    : t("EstimatesPage.drawer.createTitle")}
                </Typography>
                {isEditMode ? (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mt: 0.75, rowGap: 0.35 }}
                  >
                    <Chip
                      size="small"
                      label={
                        newPresupuesto?.NombrePresupuesto ||
                        newPresupuesto?.designCliente ||
                        "Estimate"
                      }
                      sx={{
                        height: 22,
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        maxWidth: "100%",
                      }}
                    />
                    {budget?.NumeroPresupuesto ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`#${budget.NumeroPresupuesto}`}
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          fontSize: "0.68rem",
                        }}
                      />
                    ) : null}
                  </Stack>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {missingKeys.length > 0
                      ? `${missingKeys.length} required fields remaining`
                      : "All required fields completed"}
                  </Typography>
                )}
              </Box>
              <IconButton
                onClick={handleCloseDrawer}
                aria-label="Close"
                edge="end"
              >
                <CloseRoundedIcon />
              </IconButton>
            </Stack>

            {newPresupuesto.IdPresupuesto || isEditMode ? (
              <Stack
                direction="row"
                spacing={0.65}
                sx={{
                  mt: 1.25,
                  overflowX: "auto",
                  pb: 0.25,
                  flexWrap: "nowrap",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {/* subir foto */}
                <label htmlFor="upload-estimate-file">
                  <Tooltip
                    title="Subir archivo"
                    PopperProps={{
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [0, -10],
                          },
                        },
                      ],
                    }}
                  >
                    <IconButton
                      component="span"
                      aria-label="upload"
                      sx={drawerActionIconSx}
                    >
                      <DriveFolderUploadOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </label>
                <input
                  style={{ display: "none" }}
                  id="upload-estimate-file"
                  type="file"
                  accept="image/png, image/jpeg, image/webp, application/pdf"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file)
                      return alert("Por favor, selecciona un archivo.");
                    if (!newPresupuesto.IdPresupuesto)
                      return alert(
                        "No se encontró un ID de presupuesto válido.",
                      );

                    // 🟢 1. Mostrar preview inmediata (solo para imágenes)
                    if (file.type.startsWith("image/")) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setUploadedFiles((prev) => [
                          ...prev,
                          {
                            fileName: file.name,
                            mimeType: file.type,
                            data: event.target.result.split(",")[1],
                          },
                        ]);
                      };
                      reader.readAsDataURL(file);
                    }

                    // 🟢 2. Subir archivo al backend
                    try {
                      const response = await uploadProjectFile(
                        file,
                        newPresupuesto.IdPresupuesto,
                      );
                      if (!response || !response.filePath)
                        throw new Error("La API no devolvió una ruta válida.");

                      console.log("✅ Archivo subido:", response.filePath);

                      // 🟢 3. Esperar 1,5 s para que el backend procese el archivo
                      await new Promise((r) => setTimeout(r, 1500));

                      // 🟢 4. Refrescar lista completa desde backend
                      if (budget?.NumeroPresupuesto) {
                        const updatedFiles = await getFileContent(
                          budget.NumeroPresupuesto,
                        );
                        if (updatedFiles.length > 0) {
                          setUploadedFiles(updatedFiles);
                        }
                      }
                    } catch (err) {
                      console.error("❌ Error al subir archivo:", err);
                      alert("Error al subir archivo. Intenta de nuevo.");
                    } finally {
                      // 🧹 Limpia el input para permitir re-subir el mismo archivo si se desea
                      e.target.value = "";
                    }
                  }}
                />

                {/* subir foto */}
                {/* IMPRIMIR */}
                <Tooltip
                  title="Imprimir Presupuesto"
                  PopperProps={{
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, -10],
                        },
                      },
                    ],
                  }}
                >
                  <span>
                    <BudgetPrint
                      logoBase64={miLogoBlanco}
                      idPresupuesto={newPresupuesto?.IdPresupuesto}
                      /* === Datos que queremos imprimir === */
                      fechaPresupuesto={
                        newPresupuesto?.FechaCrear ||
                        newPresupuesto?.FechaPresentacion ||
                        newPresupuesto?.FechaAceptacion ||
                        newPresupuesto?.FechaCancelacion ||
                        null
                      }
                      nombreProducto={
                        newPresupuesto?.designCliente ||
                        newPresupuesto?.DiseñoMuestra ||
                        ""
                      }
                      precioUnitario={calculateSellingPriceUN()} // <- tu cálculo
                      moneda={
                        (Array.isArray(lines) &&
                          lines.find((l) => l?.MonedaVenta)?.MonedaVenta) ||
                        "EUR"
                      }
                      unidades={Number(newPresupuesto?.Units || 0)}
                      proveedorNombre={
                        proveedores.find(
                          (p) =>
                            String(p.CODPROV) ===
                            String(newPresupuesto?.IdProveedor),
                        )?.N_PROVEEDOR || ""
                      }
                      totalVenta={calculateTotalSellingPrice()} // <- tu cálculo
                      /* === Imágenes: primero base64 (sin CORS), luego posibles URLs + sample === */
                      imageUrl={(() => {
                        const arr = Array.isArray(uploadedFiles)
                          ? uploadedFiles
                          : [];

                        // 1) data URL primero (evita CORS y 404)
                        const asDataUrls = arr
                          .filter(
                            (f) =>
                              f?.mimeType &&
                              f?.data &&
                              /^image\//i.test(f.mimeType),
                          )
                          .map((f) => `data:${f.mimeType};base64,${f.data}`);

                        // 2) URLs directas si vienen
                        const direct = arr
                          .map(
                            (f) =>
                              f?.filePath ||
                              f?.url ||
                              f?.src ||
                              f?.path ||
                              f?.href ||
                              f?.preview ||
                              null,
                          )
                          .filter(Boolean);

                        // 3) derivadas por Nº de presupuesto + fileName
                        const byNumero = budget?.NumeroPresupuesto
                          ? arr
                              .map((f) =>
                                f?.fileName
                                  ? `Proyectos/${budget.NumeroPresupuesto}/${f.fileName}`
                                  : null,
                              )
                              .filter(Boolean)
                          : [];

                        // 4) sample al final
                        return [
                          ...asDataUrls,
                          ...direct,
                          ...byNumero,
                          sampleImage,
                        ];
                      })()}
                      /* Host público para /static/media en cloud (opcional si ya tiras de data URL) */
                      assetHost="https://crm.stagedesign.app"
                      companyInfo={{
                        name: "Stage Design Studio",
                        vat: "ESB67361469",
                        address: "C/ Derechos Humanos 29",
                        city: "Sitges",
                        state: "Barcelona",
                        zip: "08870",
                        country: "España",
                        phone: "+34 935 089 690",
                        email: "info@stagedesign.es",
                        web: "https://stagedesign.es",
                      }}
                    />
                  </span>
                </Tooltip>
                {/* IMPRIMIR */}
                {/* DUPLICAR */}
                {isEditMode && (
                  <Tooltip title="Duplicar presupuesto">
                    <IconButton
                      onClick={openDuplicateDialog}
                      sx={drawerActionIconSx}
                    >
                      <ContentCopyOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {/* DUPLICAR */}
                {/* Guardar */}
                {!newPresupuesto.IdPresupuesto && (
                  <Tooltip
                    title={
                      canCreate
                        ? "Crear presupuesto"
                        : `Faltan: ${missingKeys
                            .map((k) => labelByKey[k])
                            .join(", ")}`
                    }
                  >
                    <span>
                      {" "}
                      {/* span para que el tooltip funcione con disabled */}
                      <IconButton
                        onClick={handleSavePresupuesto}
                        disabled={!canCreate}
                        sx={{
                          ...drawerActionIconSx,
                          opacity: canCreate ? 1 : 0.45,
                          "&:hover": {
                            backgroundColor: canCreate
                              ? "primary.main"
                              : "transparent",
                            color: canCreate
                              ? "primary.contrastText"
                              : "inherit",
                          },
                        }}
                      >
                        <SaveAsOutlinedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                {/* Guardar */}
                {/* VER HISTORIAL */}
                <Tooltip title="Ver presupuestos similares">
                  <IconButton
                    size="small"
                    onClick={() => setOpenHistoricos(true)}
                    sx={drawerActionIconSx}
                  >
                    <HistoryOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {/* VER HISTORIAL */}

                {/* Eliminar PRESUPUESTO */}
                {isEditMode && (
                  <Tooltip
                    title="Eliminar Presupuesto"
                    PopperProps={{
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [0, -10],
                          },
                        },
                      ],
                    }}
                  >
                    <IconButton
                      aria-label="delete"
                      size="large"
                      onClick={() => setConfirmDeleteOpen(true)} // Abrir diálogo
                      sx={{
                        ...drawerActionIconSx,
                        color: "error.main",
                        borderColor: "error.main",
                        boxShadow: `0 8px 20px ${alpha(theme.palette.error.main, 0.12)}`,
                        "&:hover": {
                          backgroundColor: "error.main",
                          color: "error.contrastText",
                        },
                      }}
                    >
                      <DeleteOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            p: { xs: 1.25, sm: 2 },
            pb:
              !newPresupuesto.IdPresupuesto && !isEditMode
                ? { xs: 2, sm: 2 }
                : { xs: isMobile ? 3 : 1.5, sm: 1.5 },
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                newPresupuesto?.IdPresupuesto || isEditMode
                  ? { xs: "1fr", xl: "minmax(280px, 320px) minmax(0, 1fr)" }
                  : "1fr",
              alignItems: "start",
              gap: { xs: 1.25, md: 1.5 },
              width: "100%",
              maxWidth: "100%",
            }}
          >
            <Box sx={{ minWidth: 0, width: "100%", maxWidth: "100%" }}>
              <BloqueProyectoEditar
                newPresupuesto={newPresupuesto}
                setNewPresupuesto={setNewPresupuesto}
                proyectos={proyectos}
                filteredProyectos={filteredProyectos}
                proveedores={proveedores}
                temporadas={temporadas}
                productosYMuestras={productosYMuestras}
                modelosTipo={modelosTipo}
                inputValue={inputValue}
                setInputValue={setInputValue}
                selectedCliente={selectedCliente}
                setSelectedCliente={setSelectedCliente}
                clientes={clientes}
                marcas={marcas}
                subgruposFromApi={subgruposFromApi}
                incoterms={incoterms}
                isEstimatesLocked={isEstimatesLocked}
                fechaContestacionLoaded={fechaContestacionLoaded}
                /*   handleManualRefresh={handleManualRefresh} */
                budget={budget}
                localDesignClient={localDesignClient}
                setLocalDesignClient={setLocalDesignClient}
                API_URL={API_URL}
                axios={axios}
                localNotas={localNotas}
                setLocalNotas={setLocalNotas}
                handleSaveNotas={handleSaveNotas}
                theme={theme}
                handleFileDelete={handleFileDelete}
                sampleImage={sampleImage}
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles} // 🟢 nueva
                uploadProjectFile={uploadProjectFile} // 🟢 nueva
                getFileContent={getFileContent}
              />
            </Box>
            {newPresupuesto?.IdPresupuesto || isEditMode ? (
              <Box
                sx={{
                  minWidth: 0,
                  width: "100%",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.divider, 0.75),
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.06)}`,
                  overflow: "visible",
                }}
              >
                <CardDerechaBolas
                  theme={theme}
                  budget={budget}
                  newPresupuesto={newPresupuesto}
                  setNewPresupuesto={setNewPresupuesto}
                  isEstimatesLocked={isEstimatesLocked}
                  setConfirmUnlockOpen={setConfirmUnlockOpen}
                  canUnlockEstimate={canUnlockEstimate}
                  handleStateChange={handleStateChange}
                  handleStateChangeClick={handleStateChangeClick}
                  API_URL={API_URL}
                  escandalloGrupos={escandalloGrupos}
                  escandalloSubgrupos={escandalloSubgrupos}
                  materialSubgrupos={subgruposFromApi}
                  lines={lines}
                  setLines={setLines}
                  refreshKey={refreshKey}
                  setRefreshKey={setRefreshKey}
                  proveedores={proveedores}
                  formatNumber={formatNumber}
                  calculateCostPriceUN={calculateCostPriceUN}
                  calculateTotalCost={calculateTotalCost}
                  calculateSellingPriceUN={calculateSellingPriceUN}
                  calculateTotalSellingPrice={calculateTotalSellingPrice}
                  calculateMarginUn={calculateMarginUn}
                  calculateTotalMargin={calculateTotalMargin}
                  calculateMarginPercentage={calculateMarginPercentage}
                  PriceChangeTwoToneIcon={PriceChangeTwoToneIcon}
                  EuroTwoToneIcon={EuroTwoToneIcon}
                  ReceiptTwoToneIcon={ReceiptTwoToneIcon}
                  BeachAccessIcon={BeachAccessIcon}
                  EstimatesLineas={EstimatesLineas}
                />
              </Box>
            ) : null}

            <VentanaPresupuestosHistoricos
              open={openHistoricos}
              onClose={() => setOpenHistoricos(false)}
              tipoPrenda={newPresupuesto?.TipoPrenda}
              idSubgrupo={newPresupuesto?.IdSubgrupo}
              idCliente={newPresupuesto?.IdCliente}
            />
          </Box>
        </Box>

        {!newPresupuesto.IdPresupuesto && !isEditMode ? (
          <Box
            sx={{
              flexShrink: 0,
              px: { xs: 1.25, sm: 2 },
              py: 1.25,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: theme.palette.background.paper,
              boxShadow: `0 -4px 16px ${alpha(theme.palette.common.black, 0.06)}`,
            }}
          >
            <Button
              variant="contained"
              fullWidth
              disabled={!canCreate}
              startIcon={<SaveAsOutlinedIcon />}
              onClick={handleSavePresupuesto}
              sx={pagePrimaryButtonSx(theme)}
            >
              {t("EstimatesPage.drawer.createTitle")}
            </Button>
          </Box>
        ) : null}
      </Box>
      <EstimateDuplicateBudgetDialog
        open={confirmDuplicateOpen}
        onClose={() => {
          if (!duplicateBusy) setConfirmDuplicateOpen(false);
        }}
        busy={duplicateBusy}
        fechaMaxima={duplicateFechaMaxima}
        fechaError={duplicateFechaError}
        onFechaMaximaChange={(value) => {
          setDuplicateFechaMaxima(value);
          if (value) setDuplicateFechaError(false);
        }}
        onConfirm={handleDuplicateBudget}
      />

      <EstimateDuplicateSuccessDialog
        open={duplicateSuccessOpen}
        onClose={() => setDuplicateSuccessOpen(false)}
        nombre={duplicateSuccessNombre}
      />

      {/* Diálogo de confirmación para desbloquear */}
      <Dialog
        open={confirmUnlockOpen}
        onClose={() => setConfirmUnlockOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {t("EstimatesPage.drawer.confirmUnlockTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("EstimatesPage.drawer.confirmUnlockBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmUnlockOpen(false)}>
            {t("EstimatesPage.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleUnlockBudget}
            color="primary"
            disabled={!canUnlockEstimate}
          >
            {t("EstimatesPage.drawer.unlock")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación de eliminacion*/}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "error.main" }}>
          {t("EstimatesPage.deleteDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("EstimatesPage.deleteDialogBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDeleteOpen(false)}>
            {t("EstimatesPage.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteBudget}
            color="error"
          >
            {t("EstimatesPage.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <EstimateOfertarChoiceDialog
        open={ofertarChoiceOpen}
        onClose={() => {
          if (!ofertarPrintBusy) setOfertarChoiceOpen(false);
        }}
        busy={ofertarPrintBusy}
        alreadyOffered={String(newPresupuesto?.Estado || "") === "Offered"}
        onChooseEmail={() => {
          setOfertarChoiceOpen(false);
          setClientOfferOpen(true);
        }}
        onChoosePrint={handleOfertarPrint}
      />

      {/* Offered → envío de oferta al cliente (solo marca Offered si el mail OK) */}
      <EstimateEnviarOfertaClienteDialog
        open={clientOfferOpen}
        onClose={() => setClientOfferOpen(false)}
        presupuesto={newPresupuesto}
        lines={lines}
        markOffered
        precioUnitario={calculateSellingPriceUN()}
        totalVenta={calculateTotalSellingPrice()}
        moneda={
          (Array.isArray(lines) &&
            lines.find((l) => l?.MonedaVenta)?.MonedaVenta) ||
          "USD"
        }
        onSent={(result) => {
          const patch = result?.presupuesto;
          if (patch?.Estado) {
            setNewPresupuesto((prev) => ({
              ...prev,
              Estado: patch.Estado,
              FechaPresentacion:
                patch.FechaPresentacion || prev.FechaPresentacion,
              ClientOfferCount: (Number(prev.ClientOfferCount) || 0) + 1,
              HasComms: true,
            }));
          } else {
            setNewPresupuesto((prev) => ({
              ...prev,
              ClientOfferCount: (Number(prev.ClientOfferCount) || 0) + 1,
              HasComms: true,
            }));
          }
          if (typeof setRefreshKey === "function") {
            setRefreshKey((k) => k + 1);
          }
        }}
      />

      <EstimateTommyOfferedDialog
        open={tommyOfferedOpen}
        onClose={() => {
          if (!tommyOfferedConfirming) setTommyOfferedOpen(false);
        }}
        confirming={tommyOfferedConfirming}
        alreadyOffered={String(newPresupuesto?.Estado || "") === "Offered"}
        onConfirm={async () => {
          setTommyOfferedConfirming(true);
          try {
            await handleStateChange("Offered");
            setTommyOfferedOpen(false);
          } finally {
            setTommyOfferedConfirming(false);
          }
        }}
      />

      {/* Diálogo de confirmación de cambio de fechas*/}
      <Dialog
        open={!!confirmStateChange}
        onClose={() => setConfirmStateChange(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {t("EstimatesPage.drawer.confirmStateTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("EstimatesPage.drawer.confirmStateBody", {
              state:
                {
                  Created: t("EstimatesPage.drawer.statusCreated"),
                  Offered: t("EstimatesPage.drawer.statusOffered"),
                  Cancelled: t("EstimatesPage.drawer.statusCancelled"),
                  Accepted: t("EstimatesPage.drawer.statusAccepted"),
                }[confirmStateChange] || confirmStateChange,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmStateChange(null)}
            color="inherit"
            variant="outlined"
          >
            {t("EstimatesPage.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleStateChange(confirmStateChange);
              setConfirmStateChange(null); // Cierra el modal
            }}
            color="primary"
          >
            {t("EstimatesPage.drawer.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default NewBudgetDrawer;
